/* promotion-wizard.js -- the Promotion view's steps ①②③ wizard, REPLACING the old
 * local-only promotion-view.js (deleted). Keeps promotion-view.js's WS-hook export
 * contract (`onPromotionProgressV2`/`onPromotionDoneV2`) and `window.__test_promotion`
 * E2E-hook convention verbatim so main.js's WS routing and the deterministic-injection
 * test pattern both survive the swap untouched.
 *
 * Architecture: this module owns a small state machine (module-private `_w`) and renders
 * it as: the Environment Ladder hero (promotion-ladder.js, driven purely via setState())
 * on top, plus a "steps" pane underneath showing ① Route / ② Release (hosted only) /
 * ③ Review (dry-run plan grouped by asset family, expandable field-level before/after
 * diffs via line-diff.js) / ④ Apply (proactive gate chips, a heavier "commitment surface"
 * for PROD targets, the real apply dispatch with a result-unknown/reconcile story, and
 * local-mode live per-asset progress via the same WS hooks). Step ③'s mount point is
 * `[data-pw-review-mount]`; step ④'s is `[data-pw-apply-mount]`.
 *
 * The wizard never branches on getAuthMode() itself -- it asks promotion-model.js's
 * createPromotionModel() for `{mode, supportsReleases, ...}` and the pure helpers it
 * already exports (`legalSegments`, `ladderGate`). Route/release picking, auto-arm, the
 * effective-source reconciliation, and the WS-hook upsert/replace semantics are all pure
 * functions of `{state, ...inputs} -> state` (exported below, mirroring promotion-
 * ladder.js's own `ladderViewModel(state)` pure-seam pattern) so promotion-wizard.test.mjs
 * can exercise the whole state machine without a DOM.
 *
 * Single-flight: every pure reducer below is a no-op (returns the SAME state reference)
 * when `state.busy` is truthy -- the wizard has no separate "disable inputs" bookkeeping
 * because DOM events call these reducers directly and a no-op reducer renders identically
 * to the current screen.
 */
import { esc, escAttr } from "./state.js";
import { iconHtml } from "./icons.js";
import { createPromotionModel, legalSegments, ladderGate, LADDER_GATE_REASON } from "./promotion-model.js";
import { createLadder, assetCountText } from "./promotion-ladder.js";
import { diffLines } from "./line-diff.js";
import { getActiveConnection } from "./auth.js";
import { renderAssistant, teardownAssistantDrawer } from "./assistant-drawer.js";
import { buildPromotionFixPrompt } from "./selection-prompt.js";
import { openUndoDialog } from "./rewind-dialog.js";
// tenants.js is reached via a DYNAMIC import (see _onLadderClick below), not a static one --
// its own graph pulls in file-tree.js -> monaco-editor.js, which touches `window` at MODULE
// LOAD time (unconditional window.addEventListener), so a static import here would break
// `node --test` for this file (no DOM/window shim in this harness). Mirrors configurator.js's
// own documented reason for dynamically importing shell-v2.js. assistant-drawer.js (and its
// own graph: state.js/icons.js/agent-store.js/agent-parts-md.js/line-diff.js/selection-
// prompt.js) is safe as a STATIC import here -- code-view.js/database-view.js already import
// it statically and are themselves statically imported by their own `node --test` suites, so
// this graph is proven window-safe at module-load time.

const ENV_ORDER = ["dev", "test", "prod"];

// ---------------------------------------------------------------------------
// Pure state machine (unit-tested seam -- promotion-wizard.test.mjs)
// ---------------------------------------------------------------------------

/**
 * Fresh wizard state. `step` 1=Route, 2=Release (hosted only), 3=Review(placeholder).
 * `families`/`picks`/`releases`/`runs` are render-support caches alongside the officially
 * tracked route/release/plan/run fields -- see the module doc comment.
 */
export function initialWizardState() {
  return {
    step: 1,
    familyPick: null,
    route: null, // {source:{id,label,env,family}, target:{id,label,env,family}} | null
    releaseId: null,
    releaseMeta: null, // the armed release row {release_id, source_tenant, source_connection_id, created_at, asset_count} | null
    planCache: new Map(),
    run: null,
    busy: null, // null | "loading" | "snapshot" | "plan" | "apply" | "reconcile" -- release-rail
    // loading is tracked separately (`releasesLoading` below), NOT as a `busy` value: see
    // `_railEpoch`'s comment. "plan" covers step ③'s model.plan() dispatch; "apply"/"reconcile"
    // cover step ④ (single-flight, like the others).
    error: "",
    families: [], // loadLadder().families cache
    picks: {}, // {dev?,test?,prod?: connId} -- multi-connection rung picks, active family only
    releases: [], // hosted releases rail cache
    runs: [], // family run history (ladderGate input for the TEST-clean badge + gateChips)
    releasesLoading: false, // true while a _loadReleasesAndRuns() fetch is in flight for the
    // CURRENT route -- render-only; deliberately NOT part of `busy` (see _railEpoch's comment).
    // ── step ④ Apply -- transient, apply-scoped state (see the "step ④ Apply" section below) ──
    applyConfirm: "", // typed PROD confirmation text for the CURRENT route.target
    dispatchAt: null, // ISO timestamp captured when the current/last apply() dispatch began --
    // feeds reconcileRunMatch's "only a run recorded AFTER this counts" rule.
    resultUnknown: false, // true after an apply fetch rejected with a non-gate (5xx/network/
    // infra) failure -- "may still be running", never rendered as a plain "failed".
    reconcileTried: false, // true once a Reconcile click has completed with no matching run --
    // drives the "still unknown, check the Timeline" follow-up line.
    applyGateError: null, // {chipId, message} | null -- a server-reported gate rejection
    // overlaid onto the one matching gate chip when that chip is still fresh-not-ok (see
    // gateChips + _applyChipsWithOverride), else surfaced as a plain banner (see
    // applyGateErrorFallback) so a genuine cross-tab race is never silently swallowed.
  };
}

/** Plan-cache key: hosted keys on release+target; local (no release concept) keys on source+target. */
export function planCacheKey(mode, route, releaseId) {
  if (!route || !route.target) return null;
  if (mode === "hosted") return `${releaseId}|${route.target.id}`;
  return `${route.source ? route.source.id : ""}|${route.target.id}`;
}

function _familyEntry(w) {
  if (!w.families || !w.families.length) return null;
  return w.families.find((f) => f.family === w.familyPick) || w.families[0];
}

function _pickedConn(rung, pickId) {
  if (!rung || !rung.length) return null;
  if (pickId != null) {
    const found = rung.find((c) => c.id === pickId);
    if (found) return found;
  }
  return rung[0];
}

function _routeEnd(fam, env, picks) {
  const conn = _pickedConn(fam.rungs[env], picks[env]);
  if (!conn) return null;
  return { id: conn.id, label: conn.label || conn.id, env, family: fam.family };
}

/** All connections in one family's rungs + unassigned tray, flattened (for label-fallback lookups). */
function _familyConnections(fam) {
  if (!fam) return [];
  return [...fam.rungs.dev, ...fam.rungs.test, ...fam.rungs.prod, ...(fam.unassigned || [])];
}

// ── route/release transitions ────────────────────────────────────────────

/**
 * route change -> clears release/plan/run, re-locks steps ②③④; lands on ② (hosted, once
 * armed) or ③-placeholder (local, or hosted with no release concept). Also clears the
 * releases/runs rail caches -- mirrors switchFamily's own reset below, so a route change is
 * never a HALF-reset: the old route's release rail must not linger under the new route while
 * _loadReleasesAndRuns() re-fetches (see that function's staleness-guard comment).
 */
export function applyRouteChange(w, mode, route) {
  return {
    ...w,
    route,
    releaseId: null,
    releaseMeta: null,
    planCache: new Map(),
    run: null,
    error: "",
    releases: [],
    runs: [],
    releasesLoading: false,
    // A new (or cleared) route makes the OLD target's apply-scoped state meaningless --
    // never a half-reset, same rationale as the fields above.
    applyConfirm: "",
    dispatchAt: null,
    resultUnknown: false,
    reconcileTried: false,
    applyGateError: null,
    step: route ? (mode === "hosted" ? 2 : 3) : 1,
  };
}

/** release change -> clears plan/run, re-locks ③④; armed release lands on ③-placeholder. */
export function applyReleaseChange(w, releaseMeta) {
  return {
    ...w,
    releaseId: releaseMeta ? releaseMeta.release_id : null,
    releaseMeta: releaseMeta || null,
    planCache: new Map(),
    run: null,
    error: "",
    // Same apply-scoped reset as applyRouteChange -- a different release is a different
    // apply attempt in waiting, even though route.target itself is unchanged.
    applyConfirm: "",
    dispatchAt: null,
    resultUnknown: false,
    reconcileTried: false,
    applyGateError: null,
    step: releaseMeta ? 3 : (w.route ? 2 : 1),
  };
}

/** Arm a route from a clicked ladder segment (from,to envs), using the active family + picks. */
export function armRouteFromSegment(w, mode, from, to) {
  if (w.busy) return w;
  const fam = _familyEntry(w);
  if (!fam) return w;
  const source = _routeEnd(fam, from, w.picks);
  const target = _routeEnd(fam, to, w.picks);
  if (!source || !target) return w;
  return applyRouteChange(w, mode, { source, target });
}

/** A station's <select> picked a different connection -- re-derive the route if that
 * station is currently one of the armed route's ends. */
export function pickStationConnection(w, mode, env, connId) {
  if (w.busy) return w;
  const fam = _familyEntry(w);
  if (!fam) return w;
  const picks = { ...w.picks, [env]: connId };
  let next = { ...w, picks };
  if (next.route) {
    const isSource = next.route.source && next.route.source.env === env;
    const isTarget = next.route.target && next.route.target.env === env;
    if (isSource || isTarget) {
      const source = isSource ? _routeEnd(fam, env, picks) : next.route.source;
      const target = isTarget ? _routeEnd(fam, env, picks) : next.route.target;
      next = applyRouteChange(next, mode, source && target ? { source, target } : null);
    }
  }
  return next;
}

/** Switch the active family -- a hard reset of everything family-scoped. */
export function switchFamily(w, family) {
  if (w.busy) return w;
  if (family === w.familyPick) return w;
  return {
    ...w,
    familyPick: family,
    picks: {},
    route: null,
    releaseId: null,
    releaseMeta: null,
    planCache: new Map(),
    run: null,
    error: "",
    step: 1,
    releases: [],
    runs: [],
    releasesLoading: false,
    applyConfirm: "",
    dispatchAt: null,
    resultUnknown: false,
    reconcileTried: false,
    applyGateError: null,
  };
}

// ── carry-forward (skip the repeat-ceremony, continue with THIS release) ────────────────

/**
 * Carry-forward eligibility: is there a next-rung-up connection this release can jump to
 * directly, skipping a repeat "arm the segment, snapshot again" cycle? Requires BOTH:
 *   (a) the release's testClean state -- `ladderGate("prod", runs, releaseId).ok`, i.e. some
 *       prior run in `runs` recorded `target_env==="test" && status==="ok"` for this release
 *       (or a wildcard match when releaseId is null, same convention ladderGate itself uses).
 *       Concretely this is exactly "run.status==='ok' at target.env==='test'" for the common
 *       single-run case (the moment right after a fresh apply); the ladderGate() delegation is
 *       the general form -- a release can have MULTIPLE prior runs, any one clean TEST run is
 *       enough, same rule PROD's own ladder gate chip already applies.
 *   (b) a legal segment (`legalSegments(rungs)`) whose `from` is THIS route's CURRENT target
 *       env -- in a 3-rung dev/test/prod ladder that's only ever meaningful when the current
 *       target env is "test" (nothing is ever legally "from prod"), AND that segment's `to`
 *       rung has at least one connection to promote to.
 * Returns the picked connection (always the FIRST one in that rung -- this pure function has
 * no `picks` input to prefer a specific one among several, unlike `_routeEnd`/`_pickedConn`;
 * a future picks-aware choice can layer on top if that ever matters in practice) shaped
 * `{id, label, env, family}` -- ready to drop straight into `carryForward(w, target)`. `null`
 * on any missing input or failed condition -- never throws.
 * @param {{source:{id,label,env,family}, target:{id,label,env,family}}|null} route
 * @param {{dev:Array, test:Array, prod:Array}} rungs the active family's rungs (buildRungs()
 *   shape -- same object `_renderAll()`/`legalSegments()` already consume)
 * @param {Array<{target_env:string, status:string, release_id?:string}>} runs prior run history
 * @param {string|null} releaseId
 * @returns {{id:string, label:string, env:string, family:string}|null}
 */
export function carryForwardTarget(route, rungs, runs, releaseId) {
  if (!route || !route.target) return null;
  if (!ladderGate("prod", runs, releaseId).ok) return null;
  const next = legalSegments(rungs).find((s) => s.from === route.target.env);
  if (!next) return null;
  const rung = rungs && rungs[next.to];
  if (!rung || !rung.length) return null;
  const conn = rung[0];
  return { id: conn.id, label: conn.label || conn.id, env: next.to, family: route.target.family };
}

/**
 * Carry-forward reducer: arms the next-rung-up segment with the SAME release -- the CURRENT
 * target becomes the new source, `target` (carryForwardTarget()'s own return) becomes the new
 * target -- and lands directly on step 3 (Review) against it, WITHOUT dispatching a plan
 * itself (the caller's own cold-dispatch, keyed by the new target, does that -- see
 * shouldDispatchPlan).
 *
 * Deliberately DEVIATES from applyRouteChange in the two ways a normal route change never
 * needs to, because this is a route change WITHIN one already-picked release's promotion
 * journey, not a fresh pick:
 *   1. releaseId/releaseMeta are KEPT (never nulled) -- "continue to PROD WITH THIS RELEASE"
 *      is the entire point of the affordance; re-deriving/re-picking a release from scratch
 *      would defeat it.
 *   2. planCache is left UNTOUCHED (not reset to a fresh Map, unlike every other route/release
 *      transition in this file) -- the cache is keyed per (release, target) (planCacheKey), so
 *      the OLD target's cached plan (if any) stays valid and reusable if the user ever
 *      navigates back to it; the NEW target's key is simply a cache miss, which
 *      shouldDispatchPlan already treats as "cold dispatch, re-plan here" on its own.
 * Every apply-scoped field (run/applyConfirm/dispatchAt/resultUnknown/reconcileTried/
 * applyGateError) resets exactly like applyRouteChange -- the OLD target's apply attempt is
 * meaningless against the NEW target. Single-flight-guarded like every other reducer; a
 * missing route/target.target or a null `target` argument is a no-op (same reference).
 * @param {object} w current wizard state
 * @param {{id:string, label:string, env:string, family:string}} target carryForwardTarget()'s
 *   own return
 * @returns {object}
 */
export function carryForward(w, target) {
  if (w.busy) return w;
  if (!w.route || !w.route.target || !target) return w;
  return {
    ...w,
    route: { source: { ...w.route.target }, target },
    run: null,
    error: "",
    applyConfirm: "",
    dispatchAt: null,
    resultUnknown: false,
    reconcileTried: false,
    applyGateError: null,
    step: 3,
  };
}

// ── auto-arm (step ① auto-completion) ────────────────────────────────────

export function routeStorageKey(family) {
  return `cpq_promo_route:${family}`;
}

/** Persist the armed segment for a family -- called on every arm (auto or manual). */
export function saveRouteChoice(storage, family, seg) {
  if (!storage || !family || !seg) return;
  try {
    storage.setItem(routeStorageKey(family), JSON.stringify({ from: seg.from, to: seg.to }));
  } catch {
    // best-effort, mirrors config.js's plain localStorage usage
  }
}

function _readSavedRoute(storage, family) {
  if (!storage || !family) return null;
  let raw;
  try {
    raw = storage.getItem(routeStorageKey(family));
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && parsed.from && parsed.to) return parsed;
  } catch {
    // corrupted value -- ignore, fall through to manual
  }
  return null;
}

/**
 * Exactly one family AND exactly one legal segment -> auto-arm it (strongest signal, skips
 * the localStorage lookup). Otherwise restore `cpq_promo_route:<family>` IF it's still a
 * legal segment for the active family; a stale/no-longer-legal saved route falls back to
 * manual (returns null -- the user picks by clicking a ladder segment).
 */
export function autoArmSegment({ families, activeFamily, storage }) {
  if (!families || !families.length) return null;
  const fam = families.find((f) => f.family === activeFamily) || families[0];
  if (!fam) return null;
  const segs = legalSegments(fam.rungs);
  if (families.length === 1 && segs.length === 1) return { from: segs[0].from, to: segs[0].to };
  const saved = _readSavedRoute(storage, fam.family);
  if (saved && segs.some((s) => s.from === saved.from && s.to === saved.to)) {
    return { from: saved.from, to: saved.to };
  }
  return null;
}

// ── effective-source rule (step ② release pick) ──────────────────────────

/** A release's ACTUAL source connection: source_connection_id first, else a label match
 * against `release.source_tenant` (older releases / cross-tool snapshots may lack the id). */
export function effectiveSourceConnection(release, connections) {
  if (!release) return null;
  if (release.source_connection_id) {
    const byId = (connections || []).find((c) => c.id === release.source_connection_id);
    if (byId) return byId;
  }
  if (release.source_tenant) {
    const byLabel = (connections || []).find((c) => c.label === release.source_tenant);
    if (byLabel) return byLabel;
  }
  return null;
}

/**
 * A picked release may have been snapshotted from a DIFFERENT connection than the one
 * currently armed as the route's source (releases are family-scoped, not source-scoped).
 * Re-derive the effective source and re-gate: if it disagrees with `route.source`, rebuild
 * the route around the release's real source, dropping the target if it's no longer a legal
 * (same-family, strictly-higher-env) pair.
 */
export function reconcileRouteForRelease(route, release, connections) {
  const eff = effectiveSourceConnection(release, connections);
  if (!eff) return { route, mismatched: false, effectiveSource: null };
  if (route && route.source && route.source.id === eff.id) {
    return { route, mismatched: false, effectiveSource: eff };
  }
  const newSource = {
    id: eff.id,
    label: eff.label || eff.id,
    env: String(eff.env_role || "").toLowerCase(),
    family: eff.family || "",
  };
  let newTarget = route ? route.target : null;
  const legal = !!newTarget
    && newTarget.family === newSource.family
    && ENV_ORDER.indexOf(newTarget.env) > ENV_ORDER.indexOf(newSource.env);
  if (!legal) newTarget = null;
  return { route: { source: newSource, target: newTarget }, mismatched: true, effectiveSource: eff };
}

/** Pick a release row from the releases rail: reconcile the source, then arm the release. */
export function pickRelease(w, releaseMeta, connections) {
  if (w.busy) return w;
  const { route, mismatched } = reconcileRouteForRelease(w.route, releaseMeta, connections);
  const based = mismatched ? { ...w, route } : w;
  return applyReleaseChange(based, releaseMeta);
}

// ── WS-hook pure helpers (progress upsert / done authoritative-copy) ─────

/** Upsert one asset's live status into `run.results` by (name,type) -- mirrors the deleted
 * promotion-view.js's onPromotionProgressV2 body exactly, against the new store's `run`. */
export function upsertAssetIntoRun(run, asset) {
  const results = Array.isArray(run && run.results) ? run.results.slice() : [];
  const i = results.findIndex((a) => a.name === asset.name && a.type === asset.type);
  if (i >= 0) results[i] = { ...results[i], ...asset };
  else results.push({ ...asset });
  return { ...(run || {}), results };
}

/** Authoritative copy of a finished run -- NEVER aliases run.results (mirrors the deleted
 * module's onPromotionDoneV2 body: "Copy (not alias) run.results ... must never mutate the
 * caller's own run object"). Graceful degradation: a degenerate `run` with no `results` array
 * (e.g. `{status:"error", error:"..."}` -- a promotion_done sent after a worker crash, before
 * any real result set was assembled) must NOT wipe the per-row progress that
 * onPromotionProgressV2's upserts already rendered. In that case `existingRun.results` (the
 * caller's PRIOR run, if any) is preserved -- copied, not aliased -- while `run`'s own fields
 * (status/error/...) are still adopted, so the UI keeps showing the last-known per-asset rows
 * under the new terminal status/error instead of blanking them out. */
export function copyRun(run, existingRun) {
  if (!run) return null;
  if (Array.isArray(run.results)) return { ...run, results: [...run.results] };
  const preserved = existingRun && Array.isArray(existingRun.results) ? [...existingRun.results] : run.results;
  return { ...run, results: preserved };
}

// ── step ③ Review: plan-cache + grouping/diff pure helpers ───────────────

/** Store `result` (a model.plan() return value) under `key` in a FRESH planCache Map --
 * never mutates the caller's Map (mirrors every other reducer's copy-on-write discipline).
 * A falsy `key` (no route/target yet -- see planCacheKey) is a no-op, same-reference return. */
export function cachePlanResult(w, key, result) {
  if (!key) return w;
  const next = new Map(w.planCache);
  next.set(key, result);
  return { ...w, planCache: next };
}

/**
 * The exact decision the review step's cold-vs-cached contract hinges on: a cached plan for
 * the CURRENT (route/release, target) key is NEVER auto-redispatched -- only reachable AND
 * cache-miss AND not-already-busy dispatches. Extracted as a pure function (rather than
 * inlined in the network-touching `_loadPlan` below) so "cached entry renders without
 * dispatch" is assertable without a DOM/network mock.
 * @param {string} mode "hosted" | "local" (matches planCacheKey's own `mode` param)
 */
export function shouldDispatchPlan(w, mode) {
  if (w.busy) return false;
  if (!w.route) return false;
  const reachable = mode === "hosted" ? !!w.releaseMeta : !!w.route;
  if (!reachable) return false;
  const key = planCacheKey(mode, w.route, w.releaseId);
  if (!key) return false;
  return !w.planCache.has(key);
}

/** The "Continue to Apply" click -> advance to step 4. Single-flight-guarded like every
 * other reducer; also refuses off step 3, or when the CURRENT cached plan is missing or
 * explicitly `allowed:false` -- the reducer itself is the gate (the UI additionally hides/
 * disables the button for the same reasons, belt-and-suspenders). */
export function continueToApply(w, mode) {
  if (w.busy) return w;
  if (w.step !== 3) return w;
  const key = planCacheKey(mode, w.route, w.releaseId);
  const plan = key ? w.planCache.get(key) : null;
  if (!plan || plan.allowed === false) return w;
  return { ...w, step: 4 };
}

// ── step ④ Apply: gate chips + confirm gating + reconcile match ──────────

/**
 * Pure client-side pre-check for step ④'s three gate chips -- mirrors (never replaces) the
 * server's own gates in promotion/gates.py + promotion/lineage.py: the server is always
 * authoritative (see the Gate-4xx handling in _onApplyClick below), this only drives the
 * proactive chip row + the Apply button's disabled state so an obviously-doomed apply never
 * gets dispatched.
 *   lineage -- re-derived from the armed segment itself (same family, strictly ascending env).
 *     Always ok in practice: armRouteFromSegment/pickStationConnection only ever arm a LEGAL
 *     segment in the first place -- this chip is still emitted (never omitted) so the row
 *     stays visually honest about what was actually checked, not just a silent assumption.
 *   ladder -- ladderGate(route.target.env, runs, releaseId) (promotion-model.js), byte-
 *     identical reason text to the Python gate (promotion/gates.py:check_ladder).
 *   confirm -- PROD targets only (the chip is entirely OMITTED for non-prod targets): pending
 *     (ok:false) until `typedConfirm` matches `route.target.label` VERBATIM (no trim/case-fold
 *     on this side -- the server's own gate strips whitespace, but the UI's own pending/ok
 *     signal deliberately does not, so a trailing space the user hasn't noticed yet still
 *     reads as "not confirmed"). The server remains authoritative regardless
 *     (promotion/gates.py:require_prod_confirmation) -- this is purely a proactive UI gate.
 * @param {{source:{env,family},target:{env,family,label}}|null} route
 * @param {Array<{target_env:string,status:string,release_id?:string}>} runs prior run history
 * @param {string|null} releaseId
 * @param {string} [typedConfirm] current value of the prod typed-confirm input
 * @returns {Array<{id:"lineage"|"ladder"|"confirm", ok:boolean, reason:string}>}
 */
export function gateChips(route, runs, releaseId, typedConfirm = "") {
  if (!route || !route.source || !route.target) return [];
  const chips = [];
  const lineageOk = route.source.family === route.target.family
    && ENV_ORDER.indexOf(route.target.env) > ENV_ORDER.indexOf(route.source.env);
  chips.push({
    id: "lineage",
    ok: lineageOk,
    reason: lineageOk ? "ok" : "This route is not a legal promotion.",
  });
  const ladder = ladderGate(route.target.env, runs, releaseId);
  chips.push({ id: "ladder", ok: ladder.ok, reason: ladder.reason });
  if (route.target.env === "prod") {
    const confirmed = typedConfirm === route.target.label;
    chips.push({
      id: "confirm",
      ok: confirmed,
      reason: confirmed ? "ok" : "Type the target name to confirm.",
    });
  }
  return chips;
}

/** Reducer-level gate for the Apply action itself -- belt-and-suspenders alongside the Apply
 * button's own `disabled` attribute (same "the reducer is the true gate" discipline as
 * continueToApply above). False while busy, off step 4, with no armed route, or when ANY
 * gate chip (lineage/ladder/confirm) reads not-ok -- in particular a PROD target can never
 * dispatch until the typed-confirm chip is satisfied. */
export function canApply(w) {
  if (w.busy) return false;
  if (w.step !== 4 || !w.route) return false;
  return gateChips(w.route, w.runs, w.releaseId, w.applyConfirm).every((c) => c.ok);
}

/** The PROD typed-confirm `<input>`'s live value -> state. Single-flight-guarded like every
 * other reducer (a disabled input shouldn't be reachable while busy, but this stays
 * consistent with the rest of the file regardless of how it's called). Also clears a stale
 * `applyGateError` that targets the "confirm" chip: the user is now editing EXACTLY the input
 * the server just rejected, so pinning the old rejection message on top of their in-progress
 * retype would be actively misleading (see applyGateOverride below for the render-side half
 * of this fix -- a chip whose fresh gateChips() computation is satisfied never shows a stale
 * message either way, but clearing it here also lets a *different* still-unsatisfied reason,
 * e.g. "Type the target name to confirm.", show through immediately instead of the server's
 * old wording). Gate errors on the OTHER chips (lineage/ladder) are untouched -- retyping the
 * confirm text doesn't address whatever made those fail.
 */
export function setApplyConfirmText(w, value) {
  if (w.busy) return w;
  const next = { ...w, applyConfirm: value };
  if (next.applyGateError && next.applyGateError.chipId === "confirm") next.applyGateError = null;
  return next;
}

// `recorded_at` is stamped by the WORKER's clock; `dispatchAt` is stamped by the BROWSER's --
// two different machines whose wall clocks are never guaranteed to agree. Without slack, a
// worker clock that merely lags the browser's by even a few seconds would make a genuinely
// matching run look like it was "recorded before dispatch" and get rejected forever. 2 minutes
// is generous for realistic wall-clock drift, yet still small relative to the runs list itself
// now that the target+release checks above already scope it to THIS apply attempt's own
// route -- the tolerance window can't accidentally pull in some earlier, unrelated run.
export const RECONCILE_CLOCK_TOLERANCE_MS = 120000;

/**
 * The RESULT-UNKNOWN reconcile matching rule: the first prior run (by list order) whose
 * `release_id` matches -- a null/undefined `releaseId` is a WILDCARD, mirroring ladderGate's
 * own convention for local mode (which has no release concept, so every local run's
 * release_id is server-minted and unknowable to the wizard ahead of time) -- AND whose
 * TARGET matches `targetId` (the run's `target_connection_id` for hosted, or `target` for
 * local's tenant-name convention -- checked against BOTH fields since the caller always knows
 * which mode it's in but this helper doesn't need to) -- runs() is family-scoped server-side
 * (see _loadReleasesAndRuns' own comment), so without this check a release re-applied to a
 * DIFFERENT target in the same family could be mistaken for this attempt's own result -- AND
 * whose `recorded_at` is at or after `dispatchAt` (the apply's own dispatch time), with
 * RECONCILE_CLOCK_TOLERANCE_MS of slack for worker/browser clock skew (see that constant's own
 * comment): a run that could only have been produced by the apply attempt just made, not some
 * earlier unrelated one. `null` when nothing matches, or when there's no `dispatchAt` to
 * reconcile against at all -- the apply-fetch's own rejection stays the last word: still
 * unknown.
 * @param {Array<{release_id?:string, recorded_at?:string, target_connection_id?:string, target?:string}>} runs
 * @param {string|null} releaseId
 * @param {string|null} dispatchAt ISO timestamp captured when the apply was dispatched
 * @param {string|null} [targetId] route.target.id -- the connection id (hosted) or tenant name
 *   (local) this apply attempt actually targeted
 * @returns {object|null}
 */
export function reconcileRunMatch(runs, releaseId, dispatchAt, targetId) {
  const dispatchMs = dispatchAt ? new Date(dispatchAt).getTime() : NaN;
  if (Number.isNaN(dispatchMs)) return null;
  for (const r of runs || []) {
    if (releaseId != null && r.release_id !== releaseId) continue;
    if (r.target_connection_id !== targetId && r.target !== targetId) continue;
    const recMs = r && r.recorded_at ? new Date(r.recorded_at).getTime() : NaN;
    if (!Number.isNaN(recMs) && recMs >= dispatchMs - RECONCILE_CLOCK_TOLERANCE_MS) return r;
  }
  return null;
}

/**
 * Prepend a just-completed apply's run record onto the run-history array (`_w.runs`) that
 * ladderGate/gateChips/carryForwardTarget all read -- `_w.runs` is otherwise ONLY refreshed via
 * a `_loadReleasesAndRuns()` network round-trip, which nothing in the apply flow itself
 * triggers, so without this a release that JUST applied cleanly to TEST would still read as
 * "not clean" (ladder gate still locked, no TEST badge, carry-forward unavailable) until some
 * UNRELATED rail refresh happened to run. The hosted/local apply routes both already return a
 * full run record (the same shape `promotionRuns()` rows have -- `engine.apply_promotion`'s own
 * record, enriched with run_id/release_id/target_env server-side), so this is a pure
 * client-side splice, no extra fetch. De-duplicates by run_id so a later real refetch (or the
 * reconcile path adopting the same run) doesn't grow the list unbounded. A run with no
 * `run_id` (never reaches this call site in practice -- a client-rejected gate response is
 * handled on a separate path) is a no-op.
 * @param {Array<object>} runs current `_w.runs`
 * @param {{run_id?:string}} run
 * @returns {Array<object>}
 */
export function appendRunToHistory(runs, run) {
  if (!run || !run.run_id) return runs || [];
  return [run, ...(runs || []).filter((r) => r.run_id !== run.run_id)];
}

/**
 * Which affordance the target-bound Ask-AI-fix button on a failed/blocked result row should
 * render: "ask" (open the wizard's own drawer straight away) or "switch" (the target
 * connection isn't the active one -- the hosted agent's sandbox rides the ACTIVE connection,
 * so the drawer can't investigate a different one; render "Switch to <target> to investigate"
 * instead, see the DOM wiring below for the handoff). Local mode has no connection binding at
 * all (a single ambient session) -- always "ask".
 * @param {"hosted"|"local"} mode
 * @param {string|null} activeCid getActiveConnection()'s current value
 * @param {string|null} targetId route.target.id
 * @returns {"ask"|"switch"}
 */
export function askAiButtonState(mode, activeCid, targetId) {
  if (mode !== "hosted") return "ask";
  return activeCid === targetId ? "ask" : "switch";
}

/**
 * "Undo this run" -- the target-connection context passed to `openUndoDialog`'s optional
 * third `conn` param ({cid, label, env_role}), so the prod typed-confirm gate reads the
 * TARGET's own label/env_role, never the active connection's (a promotion target is very
 * often NOT the active connection -- see askAiButtonState above). Design doc
 * (2026-07-11-v2-promotion-ladder-design.md, "Undo this run"): "openUndoDialog extended to
 * accept the TARGET connection explicitly ... as drafted it would preview against the
 * ACTIVE connection's journal and silently offer 'Undo 0 changes'."
 * `null` when there's no armed route (nothing to scope to) or the run has no `run_id`
 * (mirrors promotion.js's own `_runRec && _runRec.run_id` gate -- a degenerate/errored run
 * with no id was never persisted server-side, so there's nothing to undo).
 * @param {{target:{id:string,label:string,env:string}}|null} route
 * @param {{run_id?:string}|null} run
 * @returns {{cid:string,label:string,env_role:string}|null}
 */
export function undoRunConn(route, run) {
  if (!route || !route.target) return null;
  if (!run || !run.run_id) return null;
  return { cid: route.target.id, label: route.target.label, env_role: route.target.env };
}

/** `{script}` wrapper unwraps to raw script text (diffed as-is, not re-JSON-stringified);
 * anything else pretty-prints as JSON. Missing/null -> "" (the empty side of a create-only/
 * delete-only diff). CRLF-normalized so a script round-tripped through Windows line endings
 * doesn't diff as "every line changed" against an LF original -- this is the load-bearing
 * fix `diffRowsFor` depends on; without it, a CRLF-vs-LF source/target pair would spuriously
 * render as a full-file rewrite. */
function _pretty(v) {
  if (v === undefined || v === null) return "";
  const raw = typeof v?.script === "string" ? v.script : JSON.stringify(v, null, 2);
  return (raw ?? "").replace(/\r\n?/g, "\n");
}

/**
 * Unified before/after diff rows for one plan item, via line-diff.js's `diffLines`.
 * `before`/`after` missing (null/undefined) is handled explicitly rather than diffed against
 * an empty string: create-only (no before) renders EVERY line of `after` as "+" (a full add
 * block, not a spurious "-" for a phantom blank line); delete-only (no after) is the mirror
 * "-" block; neither present returns `[]` (no diff -- the row renderer uses this directly to
 * decide whether an expander exists at all, so there is exactly one source of truth for
 * "does this item have a diff").
 * @param {{before?: any, after?: any}} item
 * @returns {Array<{op: " "|"+"|"-", text: string}>}
 */
export function diffRowsFor(item) {
  const it = item || {};
  const hasBefore = it.before !== undefined && it.before !== null;
  const hasAfter = it.after !== undefined && it.after !== null;
  if (!hasBefore && !hasAfter) return [];
  const beforeText = _pretty(it.before);
  const afterText = _pretty(it.after);
  if (!hasBefore) return afterText.split("\n").map((text) => ({ op: "+", text }));
  if (!hasAfter) return beforeText.split("\n").map((text) => ({ op: "-", text }));
  return diffLines(beforeText, afterText);
}

/**
 * Group a flat plan (`model.plan()`'s `items`) by asset family (`item.type`), preserving
 * first-seen family order. `in_sync` items are tallied in `counts.in_sync` but excluded from
 * `rows` -- the render layer collapses them into a single quiet count line per group instead
 * of one row each, per the design's "grouped by asset family" review pane.
 * @param {Array<{type:string, name:string, action:string, detail?:string, before?:any, after?:any}>} items
 * @returns {Array<{familyType: string, counts: {create:number, update:number, delete:number, in_sync:number}, rows: Array}>}
 */
export function groupPlan(items) {
  const groups = [];
  const index = new Map();
  for (const raw of items || []) {
    const it = raw || {};
    const familyType = it.type || "";
    if (!index.has(familyType)) {
      index.set(familyType, groups.length);
      groups.push({ familyType, counts: { create: 0, update: 0, delete: 0, in_sync: 0 }, rows: [] });
    }
    const group = groups[index.get(familyType)];
    const action = it.action;
    if (Object.prototype.hasOwnProperty.call(group.counts, action)) group.counts[action] += 1;
    if (action !== "in_sync") group.rows.push(it);
  }
  return groups;
}

// ---------------------------------------------------------------------------
// Module-private stateful store (the DOM-facing half)
// ---------------------------------------------------------------------------

let _w = initialWizardState();
let _model = null; // createPromotionModel() instance, built once per renderPromotionInto()
let _root = null; // the mount element once rendered (or a test-injected truthy stand-in);
                   // WS hooks are a no-op while this is null -- mirrors the deleted module's
                   // own `if (!_root) return;` guard exactly.
let _ladder = null; // createLadder() instance, once the shell has been built
let _stepsEl = null;
let _drawerEl = null; // the persistent `.wb-drawer`-classed aside -- see the Ask-AI section
let _drawerBodyEl = null; // its `.wb-drawer-body` -- the mount renderAssistant() renders into
let _drawerEscHandler = null; // document-level keydown listener, wired ONLY while the drawer
// is open (see _openAssistantDrawer/_closeAssistantDrawer below) -- scoping its lifetime to
// "drawer open" rather than attaching once to `_root`/`container` at mount time avoids a
// listener leak across repeat renderPromotionInto() calls (the SAME #v2PromotionMount element
// is reused across tenant switches per shell-v2.js's own render-once-per-activation doc
// comment, so a mount-time-attached container listener would never be removed and would
// accumulate one per switch).

function _storage() {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    return null;
  }
}

function _safeRenderAll() {
  if (_root && _ladder) _renderAll();
}

// ---------------------------------------------------------------------------
// WS handlers -- live per-asset progress + the run's final result
// ---------------------------------------------------------------------------

/**
 * WS handler for promotion_progress (routed here by main.js's handleWsMessage when the v2
 * shell is active). Upserts the matching run-result row by name+type. No-op before the view
 * has ever been mounted. Step ④ (where the run/apply UI renders `_w.run`) lands in a later
 * task of this stack -- today this just keeps `_w.run` current so that UI has real data the
 * moment it exists.
 * @param {{type: string, name: string, status: string, action: string}} asset
 */
export function onPromotionProgressV2(asset) {
  if (!_root || !asset || !asset.name) return;
  _w = { ..._w, run: upsertAssetIntoRun(_w.run, asset) };
  _safeRenderAll();
}

/**
 * WS handler for promotion_done (routed here by main.js's handleWsMessage when the v2 shell
 * is active). Authoritative when `run.results` is a real array: replaces `_w.run` wholesale
 * (copied, not aliased). Gracefully degrades when it ISN'T (see copyRun's own comment) --
 * a bare `{status:"error", ...}` done event adopts its status/error but keeps whatever
 * per-asset rows onPromotionProgressV2 already built in `_w.run.results`, so a late/crashed
 * done event doesn't blank out real progress the user already saw. Always clears the wizard's
 * busy flag -- the ONLY path that unlocks a stalled apply if the HTTP response is slow/lost.
 * No-op before the view has ever been mounted.
 * @param {{status?: string, results?: Array, error?: string}} run
 */
export function onPromotionDoneV2(run) {
  if (!_root) return;
  _w = { ..._w, run: copyRun(run, _w.run), busy: null };
  _safeRenderAll();
}

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

function _shortId(id) {
  const s = String(id || "");
  return s.length > 10 ? s.slice(0, 10) : s;
}

function _relativeAge(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function _releaseChip(meta, route, runs) {
  if (!meta) return null;
  const gate = ladderGate("prod", runs, meta.release_id);
  return {
    id: meta.release_id,
    short: _shortId(meta.release_id),
    assetCount: meta.asset_count || 0,
    age: _relativeAge(meta.created_at),
    sourceEnv: route ? route.source.env : null,
    testClean: gate.ok,
  };
}

// The active family's rungs, in the shape `legalSegments()`/`carryForwardTarget()`/
// `createLadder().setState()` all consume -- factored out of `_renderAll()` so the carry-
// forward render helpers below (`_carryForwardButtonHtml`/`_releaseRowHtml`) can compute the
// same rungs without duplicating this fallback.
function _currentRungs() {
  const fam = _familyEntry(_w);
  return fam
    ? { ...fam.rungs, unassigned: fam.unassigned || [] }
    : { dev: [], test: [], prod: [], unassigned: [] };
}

function _renderAll() {
  if (!_root || !_ladder) return;
  const rungs = _currentRungs();
  const gate = ladderGate("prod", _w.runs, _w.releaseId);
  const armed = _w.route ? { fromEnv: _w.route.source.env, toEnv: _w.route.target.env } : null;
  _ladder.setState({
    rungs,
    armed,
    picks: _w.picks,
    release: _releaseChip(_w.releaseMeta, _w.route, _w.runs),
    gates: { prodLocked: !gate.ok, prodReason: gate.reason },
    busy: _w.busy === "snapshot" || _w.busy === "apply" ? _w.busy : null,
  });
  _renderSteps();
}

function _stepShellHtml(num, title, stateCls, headExtraHtml, bodyHtml) {
  return `<div class="pw-step ${stateCls}" data-pw-step="${num}">
    <div class="pw-step-hd">
      <span class="pw-step-num">${num}</span>
      <span class="pw-step-title">${esc(title)}</span>
      ${headExtraHtml || ""}
    </div>
    <div class="pw-step-body">${bodyHtml}</div>
  </div>`;
}

function _stepRouteHtml() {
  const active = _w.step === 1;
  const done = !!_w.route;
  const cls = active ? "pw-active" : done ? "pw-done" : "pw-locked";
  const body = _w.route
    ? `<div class="pw-route-summary">${esc(_w.route.source.label)} ${iconHtml("chevron-right", { size: "sm" })} ${esc(_w.route.target.label)}</div>`
    : (_w.error
      ? `<div class="pv-empty">${esc(_w.error)}</div>`
      : `<div class="pv-empty">Pick a promotion route on the ladder above.</div>`);
  return _stepShellHtml(1, "Route", cls, "", body);
}

// `.pw-release-row` stays its OWN <button> (pick semantics unchanged) but is now wrapped in a
// non-interactive `.pw-release-row-wrap` alongside a SIBLING carry-forward button (never
// nested -- two interactive elements can't legally nest) rendered only when this row's OWN
// release has an earned TEST badge AND a legal next segment exists from the CURRENTLY ARMED
// route's target env (carryForwardTarget's own eligibility rule) -- "the same affordance on
// rail release rows whose TEST badge is lit and a legal next segment exists" per the design.
function _releaseRowHtml(r) {
  const picked = _w.releaseId === r.release_id;
  const gate = ladderGate("prod", _w.runs, r.release_id);
  const badge = gate.ok
    ? `<span class="chip pw-badge-clean">${iconHtml("check", { size: "sm" })}TEST</span>`
    : "";
  const carryTarget = gate.ok ? carryForwardTarget(_w.route, _currentRungs(), _w.runs, r.release_id) : null;
  const carryHtml = carryTarget
    ? `<button type="button" class="btn btn-xs pw-carry-forward-rail" data-pw-carry-release="${escAttr(r.release_id)}" title="Continue to ${escAttr(carryTarget.label)} with this release">${iconHtml("arrow-up", { size: "sm" })}PROD</button>`
    : "";
  return `<div class="pw-release-row-wrap">
    <button type="button" class="pw-release-row${picked ? " picked" : ""}" data-pw-release="${escAttr(r.release_id)}" aria-pressed="${picked}">
      <span class="pw-rel-id mono">${esc(_shortId(r.release_id))}</span>
      <span class="pw-rel-src">${esc(r.source_tenant || "")}</span>
      <span class="pw-rel-age">${esc(_relativeAge(r.created_at))}</span>
      <span class="pw-rel-count">${esc(assetCountText(r.asset_count))}</span>
      ${badge}
    </button>
    ${carryHtml}
  </div>`;
}

function _stepReleaseHtml() {
  const reachable = !!_w.route;
  const active = _w.step === 2;
  const done = !!_w.releaseMeta;
  const cls = !reachable ? "pw-locked" : active ? "pw-active" : done ? "pw-done" : "pw-locked";
  if (!reachable) {
    return _stepShellHtml(2, "Release", cls, "", `<div class="pv-empty">Arm a route first.</div>`);
  }
  const busy = _w.busy === "snapshot";
  const snapLabel = busy ? "Snapshotting…" : `Snapshot ${esc(_w.route.source.label)}`;
  const snapBtn = `<button type="button" class="btn" data-pw-action="snapshot"${_w.busy ? " disabled" : ""}>${snapLabel}</button>`;
  // While a fetch is in flight (_w.releasesLoading), an empty `_w.releases` means "not loaded
  // yet", not "there truly are none" -- say so honestly instead of the false empty-state.
  const rows = _w.releases.length
    ? `<div class="pw-release-rail">${_w.releases.map(_releaseRowHtml).join("")}</div>`
    : _w.releasesLoading
      ? `<div class="pv-empty">Loading releases…</div>`
      : `<div class="pv-empty">No releases yet -- snapshot to create one.</div>`;
  return _stepShellHtml(2, "Release", cls, snapBtn, rows);
}

// ── Step ③ Review: grouped plan + expandable field diffs ─────────────────

const _ACTION_ICON = { create: "plus", update: "edit-3", delete: "trash-2", in_sync: "check" };
const _ACTION_LABEL = { create: "Create", update: "Update", delete: "Delete", in_sync: "In sync" };

function _actionIcon(action) {
  return _ACTION_ICON[action] || "check";
}

function _actionLabel(action) {
  return _ACTION_LABEL[action] || action || "";
}

// "global_scripts" -> "Global scripts" (first word capitalized, rest as-is) -- a plain,
// dependency-free prettifier; the exact family display name is left for a later visual
// design pass, same as the rest of this steps pane's unstyled `pw-*` classes.
function _familyLabel(familyType) {
  const words = String(familyType || "").split("_").filter(Boolean);
  if (!words.length) return "Unknown";
  return words.map((w, i) => (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w)).join(" ");
}

const _DIFF_OP_CLASS = { "+": "d-add", "-": "d-del", " ": "d-ctx" };

// Reuses workbench.css's global `.wb-diff`/`.d-add`/`.d-del`/`.d-ctx` classes verbatim (no
// import -- they're plain CSS selectors, same convention as assistant-drawer.js's own diff
// card). A real nbsp keeps the context row's op-column aligned under the +/- rows.
function _diffRowHtml(row) {
  const cls = _DIFF_OP_CLASS[row.op] || "d-ctx";
  const marker = row.op === " " ? " " : row.op;
  return `<div class="${cls}">${esc(marker)} ${esc(row.text)}</div>`;
}

function _planRowHtml(item) {
  const rows = diffRowsFor(item);
  const hasDiff = rows.length > 0;
  const toggleHtml = hasDiff
    ? `<button type="button" class="pw-plan-toggle" data-pw-diff-toggle aria-expanded="false" aria-label="Diff for ${escAttr(item.name || "this item")}">${iconHtml("chevron-right", { size: "sm" })}</button>`
    : `<span class="pw-plan-toggle pw-plan-toggle-empty"></span>`;
  const detailHtml = item.detail ? `<div class="pw-plan-detail">${esc(item.detail)}</div>` : "";
  const diffHtml = hasDiff
    ? `<div class="wb-diff pw-plan-diff" hidden>${rows.map(_diffRowHtml).join("")}</div>`
    : "";
  return `<div class="pw-plan-row" data-pw-plan-row>
    <div class="pw-plan-row-hd">
      ${toggleHtml}
      ${iconHtml(_actionIcon(item.action), { size: "sm" })}
      <span class="pw-plan-action">${esc(_actionLabel(item.action))}</span>
      <span class="pw-plan-name mono">${esc(item.name || "")}</span>
    </div>
    ${detailHtml}
    ${diffHtml}
  </div>`;
}

function _planGroupHtml(group) {
  const rowsHtml = group.rows.map(_planRowHtml).join("");
  const inSyncHtml = group.counts.in_sync
    ? `<div class="pw-plan-insync">${iconHtml("check", { size: "sm" })}<span>${group.counts.in_sync} already in sync</span></div>`
    : "";
  return `<div class="pw-plan-group">
    <div class="pw-plan-group-hd">
      ${iconHtml("layers", { size: "sm" })}
      <span class="pw-plan-group-title">${esc(_familyLabel(group.familyType))}</span>
    </div>
    ${rowsHtml}
    ${inSyncHtml}
  </div>`;
}

function _planTotals(groups) {
  const totals = { create: 0, update: 0, delete: 0, in_sync: 0 };
  for (const g of groups || []) {
    totals.create += g.counts.create;
    totals.update += g.counts.update;
    totals.delete += g.counts.delete;
    totals.in_sync += g.counts.in_sync;
  }
  return totals;
}

function _planSummaryHtml(totals) {
  return `<div class="pw-plan-summary">
    <span class="chip">${totals.create} create</span>
    <span class="chip">${totals.update} update</span>
    <span class="chip">${totals.delete} delete</span>
    <span class="chip">${totals.in_sync} in sync</span>
  </div>`;
}

function _stepReviewBusyHtml() {
  return `<div class="pv-empty pw-plan-busy" role="status" aria-live="polite">
    <div class="pw-plan-busy-line">${iconHtml("activity", { size: "sm" })}<span>Building the plan… (${_planElapsedSeconds()}s)</span></div>
    <div class="pw-plan-honest">${esc("Runs in a sandboxed worker. This can take a few minutes.")}</div>
  </div>`;
}

function _stepReviewPlanHtml(plan) {
  const items = Array.isArray(plan && plan.items) ? plan.items : [];
  const groups = groupPlan(items);
  const totals = _planTotals(groups);
  const allowed = plan ? plan.allowed !== false : true;
  const reasonHtml = !allowed
    ? `<div class="pw-plan-blocked">${iconHtml("alert-triangle", { size: "sm" })}<span>${esc(plan.reason || "This plan cannot be applied.")}</span></div>`
    : "";
  const groupsHtml = groups.length
    ? groups.map(_planGroupHtml).join("")
    : `<div class="pv-empty">Nothing to promote -- source and target already match.</div>`;
  const continueBtn = `<button type="button" class="btn" data-pw-action="continue-apply"${allowed ? "" : " disabled"}>Continue to Apply</button>`;
  return `${_planSummaryHtml(totals)}
    ${reasonHtml}
    <div class="pw-plan-groups">${groupsHtml}</div>
    <div class="pw-plan-actions">${continueBtn}</div>`;
}

// `[data-pw-review-mount]` now always wraps whatever the review step's current inner content
// is (busy / plan / error / locked) -- one stable place downstream tooling (E2E tests, the
// `injectPlan` test seam) can find regardless of state.
function _stepReviewHtml() {
  const mode = _model ? _model.mode : "local";
  const reachable = _model && _model.supportsReleases ? !!_w.releaseMeta : !!_w.route;
  const active = _w.step === 3;
  const done = _w.step > 3;
  const cls = !reachable ? "pw-locked" : active ? "pw-active" : done ? "pw-done" : "pw-locked";
  let headExtra = "";
  let inner;
  if (!reachable) {
    inner = `<div class="pv-empty">Finish the steps above first.</div>`;
  } else if (_w.busy === "plan") {
    inner = _stepReviewBusyHtml();
  } else {
    const key = planCacheKey(mode, _w.route, _w.releaseId);
    const plan = key ? _w.planCache.get(key) : null;
    if (plan) {
      headExtra = `<button type="button" class="btn btn-sm" data-pw-action="replan"${_w.busy ? " disabled" : ""}>${iconHtml("refresh-cw", { size: "sm" })}Re-plan</button>`;
      inner = _stepReviewPlanHtml(plan);
    } else if (_w.error) {
      headExtra = `<button type="button" class="btn btn-sm" data-pw-action="replan">${iconHtml("refresh-cw", { size: "sm" })}Retry plan</button>`;
      inner = `<div class="pv-empty">${esc(_w.error)}</div>`;
    } else {
      inner = `<div class="pv-empty">Preparing the plan…</div>`;
    }
  }
  const body = `<div data-pw-review-mount>${inner}</div>`;
  return _stepShellHtml(3, "Review", cls, headExtra, body);
}

// ── Step ④ Apply: gate chips, prod commitment surface, result render ─────

const _CHIP_LABEL = { lineage: "Lineage", ladder: "Ladder", confirm: "Confirm" };

/**
 * Overlay a server-reported gate rejection (`applyGateError`) onto the ONE matching chip's
 * ok/reason from a FRESH `gates` array -- but ONLY when that fresh chip is STILL not-ok. This
 * is the second (render-side) half of the stale-confirm-chip fix (the first half is
 * setApplyConfirmText clearing `applyGateError` outright once the user retypes the confirm
 * text): a chip gateChips() now computes as satisfied (e.g. the ladder gate cleared because a
 * TEST run landed clean in the meantime, or -- belt-and-suspenders -- a confirm chip that
 * became ok before setApplyConfirmText's own clear ran) must never keep showing the server's
 * old rejection message. Never mutates `gates`; a chip with no matching id, or a chip whose
 * fresh `ok` is already true, passes through untouched.
 *
 * NOT defense-in-depth for a genuine cross-tab race (the server re-derives lineage/runs at
 * apply time and rejects an apply the client's own chips still read as clean): that case is
 * DEFINED by the fresh chip already being ok, so this function is structurally a correct
 * no-op for it -- it can never attach the message anywhere in that case. See
 * applyGateErrorFallback below, which is the actual (and only) coverage for that race: the
 * plain-banner fallback so the rejection is never silently swallowed.
 * @param {Array<{id:string, ok:boolean, reason:string}>} gates gateChips()'s fresh output
 * @param {{chipId:string, message:string}|null} applyGateError
 * @returns {Array<{id:string, ok:boolean, reason:string}>}
 */
export function applyGateOverride(gates, applyGateError) {
  if (!applyGateError) return gates;
  return gates.map((c) => (c.id === applyGateError.chipId && !c.ok
    ? { ...c, ok: false, reason: applyGateError.message }
    : c));
}

/**
 * The visible-fallback half of the swallowed-rejection fix. `applyGateOverride` (above) only
 * attaches `applyGateError`'s message when the chip it targets is STILL fresh-not-ok -- correct
 * for the stale-confirm-chip case, but it means a GENUINE cross-tab race (the server re-derives
 * lineage/runs at apply time and rejects an apply the client's own stale-but-passing chips still
 * read as clean) has nowhere to attach: the chip it would target is fresh-ok, so
 * applyGateOverride is a correct no-op, and without this fallback the rejection renders NOTHING.
 * Returns the message to show as a plain banner in exactly that gap; null when there's nothing
 * to fall back for (no `applyGateError`, or its chip is genuinely still not-ok, in which case
 * the chip itself already shows it via `applyGateOverride`).
 * @param {Array<{id:string, ok:boolean, reason:string}>} freshGates gateChips()'s FRESH output
 *   (pre-override -- same array `applyGateOverride` is fed)
 * @param {{chipId:string, message:string}|null} applyGateError
 * @returns {string|null}
 */
export function applyGateErrorFallback(freshGates, applyGateError) {
  if (!applyGateError) return null;
  const chip = (freshGates || []).find((c) => c.id === applyGateError.chipId);
  if (chip && !chip.ok) return null; // the chip itself renders it via applyGateOverride
  return applyGateError.message;
}

// re-derives the chips fresh each render (never stale-cached), exactly per the "re-derive
// chips, attach the message" contract -- applyGateOverride (above) is the pure decision.
function _applyChipsWithOverride() {
  return applyGateOverride(gateChips(_w.route, _w.runs, _w.releaseId, _w.applyConfirm), _w.applyGateError);
}

function _gateChipHtml(chip) {
  const cls = chip.ok ? "ok" : "bad";
  const label = _CHIP_LABEL[chip.id] || chip.id;
  const reasonHtml = (!chip.ok && chip.reason)
    ? `<span class="pw-gate-reason">${esc(chip.reason)}</span>`
    : "";
  return `<span class="pw-gate-chip ${cls}" data-pw-gate="${escAttr(chip.id)}">
    ${iconHtml(chip.ok ? "check" : "alert-triangle", { size: "sm" })}
    <span class="pw-gate-label">${esc(label)}</span>
    ${reasonHtml}
  </span>`;
}

// The prod commitment surface -- a visibly heavier treatment (`.pw-commit-prod`, styled in
// promotion-ladder.css) wrapping the typed-confirm input. `value` is re-baked from state on
// every render, but the input itself is only ever destroyed/recreated on a DISCRETE action
// (route/release change, Apply click) -- live keystrokes are handled by a targeted DOM patch
// (_onStepsInput/_patchApplyGate below), never a full _renderSteps() rebuild, so typing never
// steals the input's own focus/cursor position.
function _commitProdHtml() {
  const label = _w.route.target.label;
  return `<div class="pw-commit-prod">
    <div class="pw-commit-head">${iconHtml("lock", { size: "sm" })}<span>Production apply</span></div>
    <div class="pw-commit-note">Type <b class="mono">${esc(label)}</b> to confirm.</div>
    <input type="text" class="pw-confirm-input" data-pw-confirm-input
      value="${escAttr(_w.applyConfirm)}" placeholder="${escAttr(label)}"
      autocomplete="off" spellcheck="false"${_w.busy ? " disabled" : ""} />
  </div>`;
}

const _RESULT_GLYPH = { applied: "check", failed: "x", blocked: "circle-pause", skipped: "circle" };
const _RESULT_LABEL = { applied: "Applied", failed: "Failed", blocked: "Blocked", skipped: "Skipped" };

// Target-bound Ask-AI-fix: only failed/blocked rows get an action at
// all -- an applied/skipped row has nothing to investigate. `askAiButtonState` (pure, tested
// standalone) decides which of the two affordances renders; this is purely the HTML for
// whichever it picks. `data-pw-result-type`/`data-pw-result-name` (not the error text itself)
// are what the click handler keys off of -- it looks the row back up in `_w.run.results` at
// click time (mirrors the `data-pw-release` id-then-lookup convention already used for the
// release rail), so a long/multiline error message never has to round-trip through an HTML
// attribute.
function _resultActionHtml(r) {
  const st = (r && r.status) || "";
  if (st !== "failed" && st !== "blocked") return "";
  const target = _w.route && _w.route.target;
  if (!target) return "";
  const mode = _model ? _model.mode : "local";
  const state = askAiButtonState(mode, getActiveConnection(), target.id);
  if (state === "switch") {
    return `<button type="button" class="btn btn-xs pw-switch-investigate" data-pw-switch-investigate data-pw-target-id="${escAttr(target.id)}">${iconHtml("external-link", { size: "sm" })}Switch to ${esc(target.label)} to investigate</button>`;
  }
  return `<button type="button" class="btn btn-xs pw-ask-ai" data-pw-ask-ai data-pw-result-type="${escAttr(r.type || "")}" data-pw-result-name="${escAttr(r.name || "")}">${iconHtml("sparkles", { size: "sm" })}Ask AI</button>`;
}

function _resultRowHtml(r) {
  const st = (r && r.status) || "";
  const glyph = _RESULT_GLYPH[st] || "circle";
  const label = _RESULT_LABEL[st] || st || "unknown";
  const durHtml = typeof (r && r.duration_s) === "number"
    ? `<span class="pw-result-dur">${r.duration_s.toFixed(2)}s</span>`
    : "";
  const errHtml = r && r.error ? `<div class="pw-result-err">${esc(r.error)}</div>` : "";
  return `<div class="pw-result-row pw-result-${escAttr(st || "unknown")}">
    ${iconHtml(glyph, { size: "sm" })}
    <span class="pw-result-name mono">${esc((r && r.name) || "")}</span>
    <span class="pw-result-type">${esc((r && r.type) || "")}</span>
    <span class="pw-result-status">${esc(label)}</span>
    ${durHtml}
    ${_resultActionHtml(r)}
    ${errHtml}
  </div>`;
}

function _resultRowsHtml(results) {
  return `<div class="pw-result-rows">${(results || []).map(_resultRowHtml).join("")}</div>`;
}

const _RUN_PILL_CLASS = { ok: "ok", partial: "partial", empty: "empty" };

// Anything outside ok/partial/empty (i.e. the engine's degenerate "error" status, see
// copyRun's own comment on that shape) reads as an error pill, not a muted/neutral one --
// it's a genuine failure, just not one this row has a more specific class for.
function _runStatusPillHtml(run) {
  const s = (run && run.status) || "";
  const cls = _RUN_PILL_CLASS[s] || "err";
  return `<span class="pw-result-pill ${cls}">${esc(s || "unknown")}</span>`;
}

// Carry-forward: the result area's PRIMARY action after a clean TEST
// apply -- "After a successful TEST apply (`run.status==='ok' && route.target.env==='test'`)"
// per the brief, verbatim. `carryForwardTarget` (pure, tested standalone) does the actual
// eligibility check (testClean + a legal next segment with a pick); this is purely the HTML.
function _carryForwardButtonHtml(run) {
  if (!run || run.status !== "ok") return "";
  if (!_w.route || _w.route.target.env !== "test") return "";
  const target = carryForwardTarget(_w.route, _currentRungs(), _w.runs, _w.releaseId);
  if (!target) return "";
  return `<div class="pw-carry-forward-wrap"><button type="button" class="btn btn-primary" data-pw-action="carry-forward">${iconHtml("arrow-up", { size: "sm" })}Continue to PROD with this release</button></div>`;
}

// "Undo this run" -- rendered whenever undoRunConn() finds a real persisted run to scope
// to (pure eligibility check, unit-tested standalone). Mirrors promotion.js's own
// #promUndoRun button verbatim (label, title, icon) so the affordance reads identically in
// both the v1 drawer and this wizard.
function _undoRunButtonHtml(run) {
  if (!undoRunConn(_w.route, run)) return "";
  return `<button type="button" class="btn btn-xs pw-undo-run" data-pw-action="undo-run" title="Undo every change this run made">${iconHtml("undo", { size: "sm" })}Undo this run</button>`;
}

// The finished-result render -- reused for BOTH the direct apply response and a reconciled
// (adopted) run: both are the same run-record shape (run_id/status/results/...).
function _applyResultHtml(run) {
  const results = Array.isArray(run.results) ? run.results : [];
  const rowsHtml = results.length ? _resultRowsHtml(results) : `<div class="pv-empty">Nothing was applied.</div>`;
  const runIdHtml = run.run_id ? `<span class="pw-result-runid mono">${esc(run.run_id)}</span>` : "";
  return `<div class="pw-apply-result">
    <div class="pw-apply-result-hd">${_runStatusPillHtml(run)}${runIdHtml}${_undoRunButtonHtml(run)}</div>
    ${_carryForwardButtonHtml(run)}
    ${rowsHtml}
  </div>`;
}

// While busy==="apply": local mode's WS hooks (onPromotionProgressV2/onPromotionDoneV2 above)
// stream per-asset rows straight into `_w.run.results` as they land -- this just renders
// whatever's there so far. Hosted has no such WS stream (`_model.supportsLiveProgress` is
// false), so this renders the activity line alone -- "hosted renders the activity animation
// only".
function _applyBusyHtml() {
  const live = (_model && _model.supportsLiveProgress
      && _w.run && Array.isArray(_w.run.results) && _w.run.results.length)
    ? _resultRowsHtml(_w.run.results)
    : "";
  return `<div class="pw-apply-busy" role="status" aria-live="polite">
    <div class="pw-apply-busy-line">${iconHtml("activity", { size: "sm" })}<span>Applying…</span></div>
    <div class="pw-apply-note">Don't navigate away during apply.</div>
    ${live}
  </div>`;
}

// RESULT-UNKNOWN: a rejected apply fetch that isn't a recognized gate rejection. Never
// rendered as "failed" -- the apply may genuinely still be running server-side. Reconcile
// (_onReconcileClick) is the only way out of this state besides starting a fresh apply.
function _resultUnknownHtml() {
  // `_w.error` here is specifically a FAILED reconcile fetch (_onReconcileClick's own catch) --
  // distinct from "reconciled, but no matching run was found yet" (reconcileTried alone).
  const reconcileErrHtml = (_w.error && _w.reconcileTried)
    ? `<div class="pw-result-unknown-note">${esc(_w.error)}</div>`
    : "";
  const note = (_w.reconcileTried && !_w.error)
    ? `<div class="pw-result-unknown-note">Still unknown. Check the Timeline for a matching run once it appears.</div>`
    : "";
  return `<div class="pw-result-unknown">
    <div class="pw-result-unknown-line">${iconHtml("alert-triangle", { size: "sm" })}<span>Result unknown. The apply may still be running.</span></div>
    <button type="button" class="btn btn-sm" data-pw-action="reconcile"${_w.busy ? " disabled" : ""}>${iconHtml("refresh-cw", { size: "sm" })}Reconcile</button>
    ${reconcileErrHtml}${note}
  </div>`;
}

function _applyBodyHtml() {
  if (!_w.route) return `<div class="pv-empty">Finish the steps above first.</div>`;
  const isProd = _w.route.target.env === "prod";
  const freshGates = gateChips(_w.route, _w.runs, _w.releaseId, _w.applyConfirm);
  const chips = applyGateOverride(freshGates, _w.applyGateError);
  const chipsHtml = `<div class="pw-gate-chips">${chips.map(_gateChipHtml).join("")}</div>`;
  const prodHtml = isProd ? _commitProdHtml() : "";
  const applyLabel = _w.busy === "apply" ? "Applying…" : "Apply";
  const applyBtn = `<button type="button" class="btn btn-primary" data-pw-action="apply"${canApply(_w) ? "" : " disabled"}>${esc(applyLabel)}</button>`;
  // A general (non-gate-chip) error -- e.g. a local "validation" gate rejection, which has no
  // chip of its own to attach to (see _gateIdForServerGate) -- renders as a plain line, same
  // idiom as steps ①③'s own `.pv-empty` error rendering. Never shown while busy==="apply"
  // (that state has its own busy line) or once a real result/resultUnknown state exists.
  const errorHtml = (_w.error && _w.busy !== "apply" && !_w.resultUnknown && !_w.run)
    ? `<div class="pv-empty">${esc(_w.error)}</div>`
    : "";
  // VISIBLE FALLBACK for a genuine cross-tab race rejection whose chip is fresh-ok (see
  // applyGateErrorFallback's own comment) -- same guards as errorHtml above; by construction
  // (applyErrorState never sets applyGateError alongside busy/resultUnknown/run) this is always
  // true whenever applyGateErrorFallback returns non-null, but kept explicit for symmetry.
  const gateFallback = applyGateErrorFallback(freshGates, _w.applyGateError);
  const gateFallbackHtml = (gateFallback && _w.busy !== "apply" && !_w.resultUnknown && !_w.run)
    ? `<div class="pv-empty">${esc(gateFallback)}</div>`
    : "";

  let stateHtml = "";
  if (_w.busy === "apply") stateHtml = _applyBusyHtml();
  else if (_w.resultUnknown) stateHtml = _resultUnknownHtml();
  else if (_w.run) stateHtml = _applyResultHtml(_w.run);

  return `${chipsHtml}${prodHtml}<div class="pw-apply-actions">${applyBtn}</div>${errorHtml}${gateFallbackHtml}${stateHtml}`;
}

function _stepApplyHtml() {
  const unlocked = _w.step >= 4;
  const cls = unlocked ? "pw-active" : "pw-locked";
  const inner = unlocked
    ? _applyBodyHtml()
    : `<div class="pv-empty">Finish the review above first.</div>`;
  const body = `<div data-pw-apply-mount>${inner}</div>`;
  return _stepShellHtml(4, "Apply", cls, "", body);
}

function _renderSteps() {
  if (!_stepsEl) return;
  const parts = [_stepRouteHtml()];
  if (_model && _model.supportsReleases) parts.push(_stepReleaseHtml());
  parts.push(_stepReviewHtml());
  parts.push(_stepApplyHtml());
  _stepsEl.innerHTML = parts.join("");
}

// ---------------------------------------------------------------------------
// Actions (network-touching)
// ---------------------------------------------------------------------------

function _allFamilyConnections() {
  return _familyConnections(_familyEntry(_w));
}

// Staleness token for _loadReleasesAndRuns(), bumped at the start of EVERY call. It is fired
// fire-and-forget from three DOM handlers (_onSegmentClick/_onStationPick/_onFamilySwitch) with
// no await, so two quick route/station/family changes can have two overlapping fetches in
// flight -- without this guard, whichever one's network round-trip happens to resolve LAST
// wins and overwrites `_w.releases`/`_w.runs` with a stale list, even if it was dispatched
// for a route the user already navigated away from.
//
// Deliberately NOT the `busy` single-flight gate the rest of the wizard's pure reducers check
// (`if (w.busy) return w;`, see the module header comment): `busy` disables ALL wizard input
// while set, but the release rail is a background refresh that must not lock the ladder while
// it loads -- the user can keep clicking segments/stations/families freely; each click starts
// a fresh fetch that (via this epoch) simply wins the race against any still-outstanding one.
// Mirrors code-view.js's `_codeEpoch` / database-view.js's `_statsEpoch`.
let _railEpoch = 0;

async function _loadReleasesAndRuns() {
  // Bumped FIRST, before the route-guard below, so that EVERY call -- including a route-less
  // one made right after a route-CLEARING transition (family switch with no auto-arm, station
  // pick that nulls the route) -- is itself an invalidation point: it cancels whatever fetch
  // was already in flight for the OLD route/family, even though this call has nothing to fetch
  // and returns immediately. Without this, a stale in-flight fetch from before the clear can
  // resolve after the clear, pass a since-unmoved epoch check, and repopulate _w.releases/runs
  // with the wrong family's data (see the DOM callers below, which now call this unconditionally
  // on every route-affecting transition, not just when a route is still armed).
  const myEpoch = ++_railEpoch;
  if (!_model || !_w.route) return;
  const cid = _w.route.source.id;
  _w = { ..._w, releasesLoading: true };
  _safeRenderAll();
  try {
    // Releases are a hosted-only concept (local has no release store; promotion-model.js's
    // local branch has no releases() call at all) -- skip that round-trip outright rather than
    // relying on api.js to resolve it to a no-op. Runs are fetched in BOTH modes: model.runs()
    // (api.promotionRuns()) already resolves the local `/api/promotion/runs` endpoint
    // regardless of cid (family-scoped server-side either way, so the source cid and the
    // target cid resolve to the SAME run history -- either works), and ladderGate()/gateChips()
    // read `_w.runs` on EVERY render, hosted or local, to decide the PROD rung's lock state --
    // a local caller that never fetched runs left the PROD chip permanently "locked" with the
    // "must apply cleanly to TEST" reason even after a genuinely clean TEST run.
    const [relRes, runRes] = await Promise.all([
      _model.supportsReleases ? _model.releases(cid) : Promise.resolve({ releases: [] }),
      _model.runs(cid),
    ]);
    if (myEpoch !== _railEpoch) return; // superseded by a newer route/station/family change
    _w = { ..._w, releases: (relRes && relRes.releases) || [], runs: (runRes && runRes.runs) || [], releasesLoading: false };
  } catch (e) {
    if (myEpoch !== _railEpoch) return; // superseded -- don't surface a stale error either
    _w = { ..._w, error: String((e && e.message) || e) || "Failed to load promotion history.", releasesLoading: false };
  }
  _safeRenderAll();
}

async function _onSnapshotClick() {
  if (_w.busy || !_w.route) return;
  _w = { ..._w, busy: "snapshot", error: "" };
  _safeRenderAll();
  try {
    const res = await _model.snapshot(_w.route.source.id);
    const releaseMeta = {
      release_id: res && res.release_id,
      source_tenant: _w.route.source.label,
      source_connection_id: _w.route.source.id,
      created_at: new Date().toISOString(),
      asset_count: (res && res.asset_count) || 0,
    };
    _w = { ..._w, busy: null };
    if (releaseMeta.release_id) _w = pickRelease(_w, releaseMeta, _allFamilyConnections());
    _safeRenderAll();
    _loadPlan(); // fire-and-forget -- the newly-armed release just became reachable for ③
    await _loadReleasesAndRuns(); // refresh the rail so the new release also appears there
  } catch (e) {
    _w = { ..._w, busy: null, error: String((e && e.message) || e) || "Snapshot failed." };
    _safeRenderAll();
  }
}

// ---------------------------------------------------------------------------
// Step ③ Review -- plan dispatch. Single-flight via `busy:"plan"` -- no separate epoch
// token needed the way `_railEpoch` is for the release rail, because `busy` already blocks
// EVERY route/release-affecting reducer for its duration (see the module header comment), so
// no second dispatch can be initiated for a DIFFERENT route/release while one is in flight.
// The one race this can't self-serialize is a tenant/connection switch mid-flight
// (`resetPromotionWizard()`, which nulls `_root`) -- guarded below the same way the WS hooks
// guard themselves: `if (!_root) return;` before writing the resolved/rejected result into
// `_w`, so a stale plan for the OLD tenant can never land in the NEW one's fresh state.
// ---------------------------------------------------------------------------

let _planStartedAt = 0; // Date.now() at dispatch start -- drives the elapsed-seconds counter
let _planTimer = null;  // setInterval id ticking the busy line's counter while busy==="plan"

function _planElapsedSeconds() {
  if (!_planStartedAt) return 0;
  return Math.max(0, Math.floor((Date.now() - _planStartedAt) / 1000));
}

function _stopPlanTimer() {
  if (_planTimer) {
    clearInterval(_planTimer);
    _planTimer = null;
  }
}

function _startPlanTimer() {
  _stopPlanTimer();
  _planStartedAt = Date.now();
  _planTimer = setInterval(() => {
    if (_w.busy !== "plan") {
      _stopPlanTimer();
      return;
    }
    _renderSteps(); // ticks the busy line's counter -- the ladder above doesn't reflect
    // plan-loading, so a full _renderAll() every second is unnecessary work.
  }, 1000);
}

/**
 * Dispatch (or re-dispatch) `model.plan(route, releaseId)` for the current route/release,
 * caching the result under `planCacheKey`. `force:true` (the Re-plan / Retry-plan click)
 * bypasses the "already cached" check but still respects the single-flight `busy` gate; every
 * cold-entry caller below passes the default `force:false`, so a cached plan is NEVER
 * auto-redispatched -- `shouldDispatchPlan` is the exact decision, exported and unit-tested
 * standalone since this function itself needs a live model + DOM to exercise end-to-end.
 */
async function _loadPlan({ force = false } = {}) {
  if (!_model) return;
  const mode = _model.mode;
  if (!force) {
    if (!shouldDispatchPlan(_w, mode)) return;
  } else if (_w.busy || !_w.route) {
    return;
  }
  const key = planCacheKey(mode, _w.route, _w.releaseId);
  if (!key) return;
  _w = { ..._w, busy: "plan", error: "" };
  _startPlanTimer();
  _safeRenderAll();
  try {
    const res = await _model.plan(_w.route, _w.releaseId);
    if (!_root) return; // wizard was reset/unmounted mid-flight -- discard rather than write
    // a stale plan into a state object a tenant switch has already replaced.
    _w = { ...cachePlanResult(_w, key, res || { items: [], allowed: true }), busy: null };
  } catch (e) {
    if (!_root) return;
    _w = { ..._w, busy: null, error: String((e && e.message) || e) || "Failed to build the plan." };
  }
  _stopPlanTimer();
  _safeRenderAll();
}

function _autoArmActiveFamily() {
  const seg = autoArmSegment({ families: _w.families, activeFamily: _w.familyPick, storage: _storage() });
  if (!seg) return;
  const next = armRouteFromSegment(_w, _model.mode, seg.from, seg.to);
  if (next !== _w) {
    _w = next;
    saveRouteChoice(_storage(), _w.familyPick, seg);
  }
}

// ---------------------------------------------------------------------------
// Step ④ Apply -- the real apply dispatch, gate-4xx / result-unknown handling, reconcile,
// and Timeline freshness. Single-flight via `busy:"apply"`/`busy:"reconcile"` like every
// other network-touching action in this file.
// ---------------------------------------------------------------------------

// A known, byte-identical server gate-reason string (or prefix) -> which of gateChips'
// three chip ids it belongs to. This is ONLY a refinement of WHICH chip a failure the caller
// has ALREADY structurally proven to be a definite, pre-dispatch rejection should be pinned
// to (see classifyApplyError below) -- it never decides unknown-vs-definite itself anymore.
// Mirrors `_gateIdForServerGate`, which classifies a RESOLVED local-mode `{error, gate}`
// rejection the same way. A message that matches NEITHER pattern still IS a definite failure
// (the caller already proved that structurally) -- it just has no honest chip to pin to, so
// it falls through to the plain `_w.error` banner instead (see _applyBodyHtml).
const _CONFIRM_GATE_PREFIX = "Type the target name";

function _classifyGateMessage(message) {
  const msg = String(message || "");
  if (msg === LADDER_GATE_REASON) return "ladder";
  if (msg.startsWith(_CONFIRM_GATE_PREFIX)) return "confirm";
  if (/family|environment role|higher environment/i.test(msg)) return "lineage";
  return null;
}

// Local mode's apply route (server/_routes_promotion.py) resolves gate rejections normally
// (HTTP 200, `{error, gate}`) rather than throwing -- this maps its explicit `gate` id onto
// one of gateChips' three chip ids. "validation" (a script syntax failure) has no chip of
// its own -- null falls through to the plain `_w.error` banner instead (see _applyBodyHtml).
function _gateIdForServerGate(serverGate) {
  if (serverGate === "ladder") return "ladder";
  if (serverGate === "prod_confirm") return "confirm";
  if (serverGate === "lineage") return "lineage";
  return null;
}

// Best-effort: forces the Timeline view (timeline-view.js) to refetch its promotion-run
// history on its next activation, so a run this wizard just produced (or just reconciled)
// doesn't sit invisible behind that view's own render-once guard. Dynamically imported --
// shell-v2.js is already part of THIS module's static importers (it statically imports
// renderPromotionInto/resetPromotionWizard from here), so a static back-import would be a
// cycle; mirrors _onLadderClick's own dynamic import of tenants.js above.
async function _refreshTimelineFreshness() {
  try {
    const mod = await import("./shell-v2.js");
    if (mod && typeof mod.resetTimelineView === "function") mod.resetTimelineView();
  } catch {
    // best-effort -- the Timeline just won't force-refresh on its next visit
  }
}

/**
 * Structural discrimination of a REJECTED apply fetch into RESULT-UNKNOWN vs DEFINITE failure
 * -- replaces the old prose-only classifier (which used to treat "message didn't match a known
 * gate string" as its ENTIRE unknown-vs-definite signal, misclassifying e.g. a plain 500 with
 * an unrecognized message as unknown even though the server definitely never dispatched).
 * Checked in this exact priority order, each a hard stop:
 *   1. `e instanceof TypeError` -- fetch() itself never reached an HTTP response (offline/DNS/
 *      CORS/aborted). No server ever weighed in, so the apply may genuinely still be running
 *      server-side -> UNKNOWN.
 *   2. `e.status` is a 4xx, OR exactly 503 -- these are the ONLY hosted-apply-route outcomes
 *      that are CONTRACTUALLY pre-dispatch (server/_routes_promotion_hosted.py's apply
 *      handler's own step-ordering comment): the three gates (lineage/prod-confirm/ladder) run
 *      FIRST and fail with 400, and 503 ("promotion jobs not configured") is the dispatch-
 *      capability check immediately AFTER the gates -- either way, definitively before any
 *      worker was ever asked to touch SAP CPQ -> DEFINITE failure. `_classifyGateMessage` is
 *      then only a REFINEMENT of a failure already known to be definite: which of the three
 *      gate chips (if any) the server's `detail`/`error` text names, purely for where in the UI
 *      to attach it -- it can never promote a definite failure back to "unknown".
 *   3. Any OTHER numeric `e.status` (500, 502, 504, ...) -- NONE of these are reserved for a
 *      pre-dispatch rejection, so they must all be treated as UNKNOWN. 502 is the route's own
 *      "a worker `error` event -> 502, NO run saved" response, which by definition fires AFTER
 *      dispatch. But the route can ALSO 500 AFTER dispatch: the worker's real apply already ran
 *      (`run_svc.run_promotion_apply` returned a result, meaning SAP CPQ was already mutated),
 *      and the run-persist step right after it (`PostgresPromotionStore.save_run`) is un-caught
 *      -- a DB hiccup there surfaces as a plain 500 with the target tenant already mutated and
 *      NO PromotionRun saved for Reconcile to find. Rendering "failed" for that case would be
 *      false certainty on a PROD apply, so every other 5xx (and any future one) gets the same
 *      conservative treatment as a network drop -> UNKNOWN.
 *   4. No `.status` at all and not a TypeError (some other unexpected throw shape) -- nothing
 *      structural to prove either way -> conservative UNKNOWN, same treatment as a network
 *      drop.
 * @param {Error & {status?: number}} e
 * @returns {{kind: "unknown"|"definite", chipId: ("lineage"|"ladder"|"confirm"|null)}}
 */
export function classifyApplyError(e) {
  if (e instanceof TypeError) return { kind: "unknown", chipId: null };
  const status = e && e.status;
  if (typeof status === "number" && ((status >= 400 && status < 500) || status === 503)) {
    return { kind: "definite", chipId: _classifyGateMessage(e && e.message) };
  }
  return { kind: "unknown", chipId: null };
}

/**
 * The catch-path state write for a rejected apply fetch -- factored out of _onApplyClick's own
 * try/catch (rather than inlined there) so BOTH the WS race-guard below AND the
 * classifyApplyError-driven state mapping are drivable directly by promotion-wizard.test.mjs,
 * without a real fetch rejection.
 *
 * RACE GUARD: a `promotion_done` WS event can resolve (or gracefully degrade, see copyRun's
 * own comment) this SAME apply attempt while its HTTP round-trip is still in flight --
 * onPromotionDoneV2 ALWAYS clears `busy` to null the instant it lands (that's its documented
 * job: "the ONLY path that unlocks a stalled apply if the HTTP response is slow/lost"). If
 * `w.busy` is no longer `"apply"` by the time this runs, the WS path already won the race and
 * wrote the authoritative run into `w.run` -- this is a no-op (same-reference return) so a
 * slow/losing HTTP rejection can never stomp a real, already-rendered result with a stale
 * resultUnknown/gate-error banner.
 * @param {object} w current wizard state
 * @param {Error & {status?: number}} e the rejected apply fetch's error
 * @returns {object} the SAME `w` reference (race -- no-op) or a new state
 */
export function applyErrorState(w, e) {
  if (w.busy !== "apply") return w; // already resolved by a racing onPromotionDoneV2 -- no-op
  const message = String((e && e.message) || e) || "Apply failed.";
  const { kind, chipId } = classifyApplyError(e);
  if (kind === "unknown") return { ...w, busy: null, resultUnknown: true };
  if (chipId) return { ...w, busy: null, applyGateError: { chipId, message } };
  return { ...w, busy: null, error: message };
}

/**
 * Dispatch the real apply. Three outcomes, matched to the brief's own vocabulary:
 *  - resolves with a real run record -> reuse onPromotionDoneV2's own store path (the SAME
 *    function the WS `promotion_done` hook calls) so local's WS-streamed + HTTP-resolved
 *    paths land through one code path, idempotently.
 *  - resolves with a client-recognizable gate rejection (local mode's `{error, gate}` 200,
 *    or a THROWN, structurally-definite Error -- see classifyApplyError) -> attach the
 *    server's reason AT the failing gate chip, not a toast.
 *  - a thrown, structurally-UNKNOWN Error (network drop, or the hosted apply route's own 502
 *    -- see classifyApplyError) -> RESULT-UNKNOWN, never rendered as "failed".
 */
async function _onApplyClick() {
  if (!canApply(_w)) return;
  const isProd = _w.route.target.env === "prod";
  _w = {
    ..._w,
    busy: "apply",
    error: "",
    resultUnknown: false,
    reconcileTried: false,
    applyGateError: null,
    dispatchAt: new Date().toISOString(),
  };
  _safeRenderAll();
  let res;
  try {
    res = await _model.apply(_w.route, _w.releaseId, isProd ? { confirm: _w.applyConfirm } : {});
  } catch (e) {
    if (!_root) return; // wizard was reset/unmounted mid-flight -- discard, see _loadPlan's own guard
    const next = applyErrorState(_w, e);
    if (next === _w) return; // race guard -- see applyErrorState's own comment
    _w = next;
    _safeRenderAll();
    return;
  }
  if (!_root) return;
  if (res && res.gate) {
    const chipId = _gateIdForServerGate(res.gate);
    _w = chipId
      ? { ..._w, busy: null, applyGateError: { chipId, message: res.error || "This promotion was blocked." } }
      : { ..._w, busy: null, error: res.error || "This promotion was blocked." };
    _safeRenderAll();
    return;
  }
  // Splice the just-completed run into `_w.runs` BEFORE onPromotionDoneV2's own render --
  // ladderGate/gateChips/carryForwardTarget all read `_w.runs`, which nothing else in this
  // flow refreshes (see appendRunToHistory's own comment); without this, a release that just
  // applied cleanly to TEST would still read as "not clean" (no TEST badge, PROD's ladder
  // chip still locked, no carry-forward affordance) until some unrelated rail refresh ran.
  if (res && res.run_id) _w = { ..._w, runs: appendRunToHistory(_w.runs, res) };
  onPromotionDoneV2(res); // reuses the SAME store path as the WS `promotion_done` hook.
  if (res && res.run_id) await _refreshTimelineFreshness(); // a real run was persisted.
}

/**
 * RESULT-UNKNOWN recovery: re-fetch the target's run history and adopt whichever run
 * reconcileRunMatch (pure, unit-tested) says matches this apply attempt. No match keeps the
 * wizard in resultUnknown -- `reconcileTried` then drives the "still unknown" follow-up line.
 */
async function _onReconcileClick() {
  if (!_w.route || _w.busy) return;
  _w = { ..._w, busy: "reconcile", error: "" }; // clear any stale error from a prior attempt
  _safeRenderAll();
  try {
    const res = await _model.runs(_w.route.target.id);
    if (!_root) return;
    const freshRuns = (res && res.runs) || _w.runs;
    const match = reconcileRunMatch(freshRuns, _w.releaseId, _w.dispatchAt, _w.route.target.id);
    if (match) {
      // Adopt the FULL fresh runs list too, not just the matched run -- same rationale as
      // _onApplyClick's appendRunToHistory splice: carryForwardTarget/gateChips need `_w.runs`
      // to reflect this apply attempt's own outcome once it's known.
      _w = { ..._w, busy: null, resultUnknown: false, reconcileTried: false, run: copyRun(match, _w.run), runs: freshRuns };
      _safeRenderAll();
      await _refreshTimelineFreshness();
      return;
    }
    _w = { ..._w, busy: null, reconcileTried: true };
  } catch (e) {
    if (!_root) return;
    _w = { ..._w, busy: null, reconcileTried: true, error: String((e && e.message) || e) || "Reconcile failed." };
  }
  _safeRenderAll();
}

// ---------------------------------------------------------------------------
// DOM wiring
// ---------------------------------------------------------------------------

// ── Assistant drawer (target-bound Ask-AI-fix) ────────────────────────────────────────
//
// Contract (REQUIRED, not a style choice): `renderAssistant`'s own GC (assistant-drawer.js) is
// a MutationObserver watching a persistent `.wb-drawer`-classed ANCESTOR's `hidden` attribute
// -- `bodyEl.closest(".wb-drawer")`. `_drawerEl` below is built with the LITERAL class
// `wb-drawer` for exactly this reason (not a lookalike class); closing it by setting `hidden`
// (never by tearing down/replacing the element) is what lets renderAssistant() detect the
// close and unsubscribe its own streaming-answer subscription. Reuses `.wb-drawer`/
// `.wb-drawer-head`/`.wb-drawer-body`'s existing CSS (workbench.css, scoped under #appV2, not
// under `.wb` -- confirmed by inspection) for free, same "reuse the global class verbatim"
// precedent as this file's own `.wb-diff` usage in the plan-diff rows above.
function _openAssistantDrawer(ctx) {
  if (!_drawerEl || !_drawerBodyEl) return;
  _drawerEl.removeAttribute("hidden");
  renderAssistant(ctx, _drawerBodyEl);
  if (!_drawerEscHandler) {
    _drawerEscHandler = (e) => { if (e.key === "Escape") _closeAssistantDrawer(); };
    document.addEventListener("keydown", _drawerEscHandler);
  }
}

function _closeAssistantDrawer() {
  if (_drawerEl) {
    _drawerEl.setAttribute("hidden", ""); // triggers renderAssistant()'s own MutationObserver teardown
    if (_drawerBodyEl) _drawerBodyEl.innerHTML = "";
  }
  if (_drawerEscHandler) {
    document.removeEventListener("keydown", _drawerEscHandler);
    _drawerEscHandler = null;
  }
}

// ── Carry-forward ─────────────────────────────────────────────────────────────────────

// Shared tail for both carry-forward entry points (the result area's primary action AND the
// rail release row's mini action) -- arms the new segment via the `carryForward` reducer, then
// mirrors _onSegmentClick's own post-arm side effects (persist the route choice, refresh the
// rail, cold-dispatch the plan at the new (release, target) key).
function _doCarryForward(baseW, target) {
  const next = carryForward(baseW, target);
  if (next === baseW) return;
  _w = next;
  saveRouteChoice(_storage(), _w.familyPick, { from: _w.route.source.env, to: _w.route.target.env });
  _safeRenderAll();
  // Unconditional (not `if (_model.supportsReleases) ...`) -- _loadReleasesAndRuns() itself
  // now decides what to fetch per mode (releases hosted-only, runs both), so gating the call
  // here would just re-introduce the local-mode "runs never refresh" bug this fix closed.
  _loadReleasesAndRuns();
  _loadPlan();
}

function _onSegmentClick(from, to) {
  const next = armRouteFromSegment(_w, _model.mode, from, to);
  if (next === _w) return;
  _w = next;
  if (_w.route) saveRouteChoice(_storage(), _w.familyPick, { from, to });
  _safeRenderAll();
  // Unconditional (not `if (_w.route && ...)`, and not `if (_model.supportsReleases) ...`):
  // armRouteFromSegment never itself nulls the route (an illegal segment is a no-op that
  // returns before this line), but this is kept symmetric with _onStationPick/_onFamilySwitch
  // below -- both of which CAN reach here with a cleared route -- so the invariant ("every
  // route-affecting DOM transition reaches _loadReleasesAndRuns() for its epoch-bump/
  // cancellation effect") doesn't silently depend on armRouteFromSegment's current
  // no-clearing behavior. The supportsReleases mode split now lives INSIDE
  // _loadReleasesAndRuns() itself (releases hosted-only, runs both), so gating the call here
  // too would skip the runs fetch in local mode entirely.
  _loadReleasesAndRuns();
  _loadPlan(); // no-op unless this landed on a reachable ③ (local mode's route-only case);
  // hosted mode isn't reachable yet here (no release picked) so this is a cheap no-op there.
}

function _onStationPick(env, connId) {
  const next = pickStationConnection(_w, _model.mode, env, connId);
  if (next === _w) return;
  _w = next;
  if (_w.route) {
    saveRouteChoice(_storage(), _w.familyPick, { from: _w.route.source.env, to: _w.route.target.env });
  }
  _safeRenderAll();
  // Unconditional -- see _onSegmentClick's comment: a station pick can null the route
  // (previously-armed end no longer resolvable), and that clearing call must still reach
  // _loadReleasesAndRuns() to cancel any stale in-flight fetch via the epoch bump (not gated
  // on supportsReleases either, same reason).
  _loadReleasesAndRuns();
  _loadPlan();
}

function _onFamilySwitch(family) {
  const next = switchFamily(_w, family);
  if (next === _w) return;
  _w = next;
  _autoArmActiveFamily();
  _safeRenderAll();
  // Unconditional -- see _onSegmentClick's comment: switching families always clears the OLD
  // family's route/releases/runs; when auto-arm doesn't land a new route, this call must still
  // reach _loadReleasesAndRuns() so its epoch bump invalidates the old family's in-flight fetch
  // (not gated on supportsReleases either, same reason).
  _loadReleasesAndRuns();
  _loadPlan();
}

// The ladder's first-run onboarding button (`[data-action="open-settings"]`) is
// intentionally left unwired by promotion-ladder.js itself (pure render, no settings-dialog
// knowledge) -- this module attaches the click here. There is no dedicated "connection
// editor" dialog in this app; the nearest honest affordance is the tenant/connection
// switcher dropdown (tenants.js:toggleTenantDropdown), which already carries an inline
// "Edit family / environment" editor per row (local mode) and the full connection list
// (hosted mode) -- exactly the lineage data this ladder needs assigned.
function _onLadderClick(e) {
  if (e.target.closest('[data-action="open-settings"]')) {
    import("./tenants.js").then((m) => m.toggleTenantDropdown()).catch(() => {});
  }
}

function _onStepsClick(e) {
  if (e.target.closest('[data-pw-action="snapshot"]')) {
    _onSnapshotClick();
    return;
  }
  if (e.target.closest('[data-pw-action="replan"]')) {
    _loadPlan({ force: true }); // explicit user action -- the ONLY way a cached plan refetches
    return;
  }
  if (e.target.closest('[data-pw-action="continue-apply"]')) {
    const mode = _model ? _model.mode : "local";
    const next = continueToApply(_w, mode);
    if (next !== _w) {
      _w = next;
      _safeRenderAll();
    }
    return;
  }
  if (e.target.closest('[data-pw-action="apply"]')) {
    _onApplyClick();
    return;
  }
  if (e.target.closest('[data-pw-action="reconcile"]')) {
    _onReconcileClick();
    return;
  }
  if (e.target.closest('[data-pw-action="carry-forward"]')) {
    // Result area's primary action (Deliverable A) -- carries the CURRENT route/release
    // forward. `_carryForwardButtonHtml` only ever renders this button when
    // carryForwardTarget() is already non-null, but the reducer re-derives it here too (single
    // source of truth for the eligibility check, never trust the DOM alone).
    if (_w.busy) return;
    const target = carryForwardTarget(_w.route, _currentRungs(), _w.runs, _w.releaseId);
    if (target) _doCarryForward(_w, target);
    return;
  }
  const carryReleaseBtn = e.target.closest("[data-pw-carry-release]");
  if (carryReleaseBtn) {
    // Rail row's "→ PROD" mini action -- the release being carried forward is THIS row's own
    // release, which may not be the currently-picked one, so pick it first (same effect as a
    // plain `data-pw-release` click) before recomposing the route on top of it.
    if (_w.busy) return;
    const id = carryReleaseBtn.getAttribute("data-pw-carry-release");
    const target = carryForwardTarget(_w.route, _currentRungs(), _w.runs, id);
    if (!target) return;
    const meta = _w.releases.find((r) => r.release_id === id);
    _doCarryForward({ ..._w, releaseId: id, releaseMeta: meta || _w.releaseMeta }, target);
    return;
  }
  const askAiBtn = e.target.closest("[data-pw-ask-ai]");
  if (askAiBtn) {
    const type = askAiBtn.getAttribute("data-pw-result-type") || "";
    const name = askAiBtn.getAttribute("data-pw-result-name") || "";
    const results = _w.run && Array.isArray(_w.run.results) ? _w.run.results : [];
    const row = results.find((x) => x.type === type && x.name === name);
    const prompt = buildPromotionFixPrompt({
      type, name, error: row ? row.error : "",
      source: _w.route ? _w.route.source.label : "",
      target: _w.route ? _w.route.target.label : "",
    });
    _openAssistantDrawer({ kind: "explain", label: `${type} ${name}`, prompt });
    return;
  }
  const undoRunBtn = e.target.closest('[data-pw-action="undo-run"]');
  if (undoRunBtn) {
    // Re-derives the same eligibility check the button's own render already passed (never
    // trust the DOM alone -- same discipline as the carry-forward/ask-ai handlers above).
    const conn = undoRunConn(_w.route, _w.run);
    if (conn) {
      openUndoDialog({ run_id: _w.run.run_id }, () => { _loadReleasesAndRuns(); }, conn);
    }
    return;
  }
  const switchBtn = e.target.closest("[data-pw-switch-investigate]");
  if (switchBtn) {
    const targetId = switchBtn.getAttribute("data-pw-target-id");
    if (targetId) {
      // Hosted only (askAiButtonState never returns "switch" in local mode). switchConnection()
      // (tenants.js) fires resetViewGuards() (shell-v2.js), which calls resetPromotionWizard()
      // and forces a fresh renderPromotionInto() on the Promotion view's next activation -- this
      // wizard instance is torn down entirely, including this run's results. The handoff that
      // survives: autoArmSegment()/saveRouteChoice()'s localStorage last-used-route mechanism
      // (this file's own "auto-arm" section above) re-arms the SAME route
      // automatically once the switched-to connection's ladder loads, so the user lands back on
      // the same route/step instead of a blank step ①. They still need a fresh Ask-AI click on
      // the target's OWN failed/blocked assets once there -- this run's per-asset results don't
      // (and can't) survive the remount, only the ROUTE choice does.
      import("./tenants.js").then((m) => m.switchConnection(targetId)).catch(() => {});
    }
    return;
  }
  const diffToggle = e.target.closest("[data-pw-diff-toggle]");
  if (diffToggle) {
    // Direct DOM toggle (no state/re-render) -- mirrors run-traces.js's own decision-row
    // accordion: flipping `hidden` in place keeps every OTHER row's expand state intact,
    // which a full _renderSteps() rebuild would otherwise lose.
    const row = diffToggle.closest("[data-pw-plan-row]");
    const diffEl = row && row.querySelector(".pw-plan-diff");
    if (diffEl) {
      diffEl.hidden = !diffEl.hidden;
      diffToggle.setAttribute("aria-expanded", diffEl.hidden ? "false" : "true");
      diffToggle.innerHTML = iconHtml(diffEl.hidden ? "chevron-right" : "chevron-down", { size: "sm" });
    }
    return;
  }
  const relBtn = e.target.closest("[data-pw-release]");
  if (relBtn) {
    if (_w.busy) return;
    const id = relBtn.getAttribute("data-pw-release");
    const meta = _w.releases.find((r) => r.release_id === id);
    if (meta) {
      _w = pickRelease(_w, meta, _allFamilyConnections());
      _safeRenderAll();
      _loadPlan(); // the newly-picked release just became reachable for ③
    }
  }
}

// The PROD typed-confirm `<input>`'s live keystrokes -> state, via a TARGETED DOM patch
// (_patchApplyGate below), not a full _renderSteps() rebuild -- rebuilding the steps pane's
// innerHTML on every keystroke would destroy and recreate the input itself, stealing its own
// focus/cursor position mid-type. Mirrors the diff-toggle handler's own "direct DOM, no
// state/re-render" idiom in _onStepsClick above, just for an `input` event instead of `click`.
function _onStepsInput(e) {
  const input = e.target.closest("[data-pw-confirm-input]");
  if (!input) return;
  const next = setApplyConfirmText(_w, input.value);
  if (next === _w) return;
  _w = next;
  _patchApplyGate();
}

// Patches ONLY the confirm chip + the Apply button's disabled state -- the two nodes whose
// rendered output actually depends on `_w.applyConfirm` -- leaving the confirm `<input>` (and
// everything else in the steps pane) untouched.
function _patchApplyGate() {
  if (!_stepsEl) return;
  const chipEl = _stepsEl.querySelector('[data-pw-gate="confirm"]');
  if (chipEl) {
    const chip = _applyChipsWithOverride().find((c) => c.id === "confirm");
    if (chip) chipEl.outerHTML = _gateChipHtml(chip);
  }
  const applyBtn = _stepsEl.querySelector('[data-pw-action="apply"]');
  if (applyBtn) applyBtn.disabled = !canApply(_w);
}

function _buildShell(container) {
  container.innerHTML = `
    <div class="content-head">
      <span class="ch-title">Promotion</span>
      <span class="ch-sub">Move config between environments</span>
    </div>
    <div class="pw-body">
      <div class="pw-main">
        <div class="pw-ladder-mount" data-pw-ladder></div>
        <div class="pw-steps" data-pw-steps></div>
      </div>
      <aside class="wb-drawer" hidden data-pw-drawer>
        <div class="wb-drawer-head">
          <span class="ch-title">Assistant</span>
          <button type="button" class="icon-btn wb-drawer-close" data-pw-drawer-close aria-label="Close">${iconHtml("x", { size: "sm" })}</button>
        </div>
        <div class="wb-drawer-body" data-pw-drawer-body role="log" aria-live="polite"></div>
      </aside>
    </div>`;

  const ladderMount = container.querySelector("[data-pw-ladder]");
  _stepsEl = container.querySelector("[data-pw-steps]");
  _drawerEl = container.querySelector("[data-pw-drawer]");
  _drawerBodyEl = container.querySelector("[data-pw-drawer-body]");

  _ladder = createLadder(ladderMount, {
    families: (_w.families || []).map((f) => ({ family: f.family })),
    onFamilySwitch: _onFamilySwitch,
  });
  _ladder.onSegmentClick(_onSegmentClick);
  _ladder.onStationPick(_onStationPick);
  ladderMount.addEventListener("click", _onLadderClick);
  _stepsEl.addEventListener("click", _onStepsClick);
  _stepsEl.addEventListener("input", _onStepsInput);
  const drawerCloseBtn = container.querySelector("[data-pw-drawer-close]");
  if (drawerCloseBtn) drawerCloseBtn.addEventListener("click", _closeAssistantDrawer);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Fetch + render the Promotion wizard into `container` (v2's #v2PromotionMount).
 * Render-once compatible -- shell-v2.js guards against re-render on repeat nav clicks, and
 * calls `resetPromotionWizard()` (below) on a tenant/connection switch so the next
 * activation starts clean.
 */
export async function renderPromotionInto(container) {
  if (!container) return;
  _root = container;
  _model = createPromotionModel();
  container.innerHTML = `<div class="pv-empty">Loading promotion…</div>`;
  _w = { ...initialWizardState(), busy: "loading" };
  try {
    const { families } = await _model.loadLadder();
    _w = { ..._w, families, busy: null };
  } catch (e) {
    _w = { ..._w, families: [], busy: null, error: String((e && e.message) || e) || "Failed to load connections." };
  }
  _w.familyPick = _w.families.length ? _w.families[0].family : null;
  _autoArmActiveFamily();
  _buildShell(container);
  _renderAll();
  // Not gated on `_model.supportsReleases` (only on `_w.route`, which _autoArmActiveFamily()
  // may have just armed): _loadReleasesAndRuns() itself decides releases-vs-runs per mode, and
  // local mode needs this call too -- it's the FIRST chance `_w.runs` (empty since
  // initialWizardState()) gets populated, and ladderGate()/gateChips() read it on every render
  // (including the `_renderAll()` just above) to decide the PROD rung's lock state. Skipping
  // this call in local mode left the ladder showing a locked PROD chip on first paint even when
  // a clean TEST run already existed.
  if (_w.route) await _loadReleasesAndRuns();
  await _loadPlan(); // local mode's auto-arm can land directly on a reachable ③; no-op otherwise
}

/**
 * Reset the wizard to a clean, unmounted state -- called by shell-v2.js's
 * resetViewGuards() on every tenant/connection switch (mirrors resetDatabaseStats()/
 * resetCodeView() there) so a stale route/release/plan from the OLD tenant never survives
 * into the NEW one, and so WS hooks go back to their pre-mount no-op until the view is
 * actually reactivated.
 */
export function resetPromotionWizard() {
  _stopPlanTimer();
  // container.innerHTML gets wiped wholesale on the next renderPromotionInto() (tenant switch)
  // without ever going through a closeDrawer()-style call, so the assistant drawer's own
  // MutationObserver-based close detection (assistant-drawer.js) never fires for it -- force
  // the teardown here too (mirrors code-view.js's resetCodeView()/database-view.js's own
  // teardownAssistantDrawer() call for the exact same reason). A no-op under `node --test`
  // (no DOM ever mounted there, so `_activeTeardown` inside assistant-drawer.js is always null).
  teardownAssistantDrawer();
  if (_drawerEscHandler) {
    document.removeEventListener("keydown", _drawerEscHandler);
    _drawerEscHandler = null;
  }
  _w = initialWizardState();
  _model = null;
  _root = null;
  _ladder = null;
  _stepsEl = null;
  _drawerEl = null;
  _drawerBodyEl = null;
}

// ---------------------------------------------------------------------------
// Test hooks (E2E determinism + Node unit tests)
// ---------------------------------------------------------------------------
// The functions below are exported both as plain named exports (so
// promotion-wizard.test.mjs can drive them under `node --test`, where `window` does not
// exist) AND assigned to `window.__test_promotion` (so a browser E2E test gets the exact
// same migrated hook promotion-view.js used to expose). Not part of the module's app-facing
// API -- main.js/shell-v2.js only ever import the four exports documented above.

export function _getStateForTest() {
  return {
    step: _w.step,
    familyPick: _w.familyPick,
    route: _w.route ? { source: { ..._w.route.source }, target: { ..._w.route.target } } : null,
    releaseId: _w.releaseId,
    releaseMeta: _w.releaseMeta ? { ..._w.releaseMeta } : null,
    planCache: Array.from(_w.planCache.entries()),
    run: _w.run
      ? { ..._w.run, results: Array.isArray(_w.run.results) ? _w.run.results.map((r) => ({ ...r })) : _w.run.results }
      : null,
    busy: _w.busy,
    error: _w.error,
    families: (_w.families || []).map((f) => ({ family: f.family })),
    releases: _w.releases.map((r) => ({ ...r })),
    runs: _w.runs.map((r) => ({ ...r })),
    releasesLoading: _w.releasesLoading,
    mode: _model ? _model.mode : null,
    applyConfirm: _w.applyConfirm,
    dispatchAt: _w.dispatchAt,
    resultUnknown: _w.resultUnknown,
    reconcileTried: _w.reconcileTried,
    applyGateError: _w.applyGateError ? { ..._w.applyGateError } : null,
  };
}

// Injects a fixed loadLadder()-shaped fixture (`{families:[...]}` or a bare families array)
// bypassing the network fetch, then re-runs the same auto-arm logic a real load would.
// Requires the wizard to already be mounted (a prior renderPromotionInto() call).
export function _setLadderForTest(fixture) {
  if (!_model) _model = createPromotionModel();
  const families = Array.isArray(fixture) ? fixture : (fixture && fixture.families) || [];
  _w = {
    ...initialWizardState(),
    families,
    familyPick: families.length ? families[0].family : null,
  };
  _autoArmActiveFamily();
  _safeRenderAll();
}

// Seam for step ③ (this task) + E2E determinism generally: stashes a plan result under the
// CURRENT plan-cache key so the review step renders deterministically without a real
// model.plan() network call, the same way `setLadder` above bypasses loadLadder(). Built on
// the same `cachePlanResult` pure helper the real dispatch path (`_loadPlan`) uses.
export function _injectPlanForTest(fixture) {
  const mode = _model ? _model.mode : "local";
  const key = planCacheKey(mode, _w.route, _w.releaseId);
  if (!key) return;
  _w = cachePlanResult(_w, key, fixture);
  _safeRenderAll();
}

// Node-only seam (no DOM): lets promotion-wizard.test.mjs flip the WS hooks' mounted-guard
// without driving a real renderPromotionInto() (which needs a live DOM + network).
export function _setMountedForTest(mounted) {
  _root = mounted ? (_root || {}) : null;
}

if (typeof window !== "undefined") {
  window.__test_promotion = {
    getState: _getStateForTest,
    setLadder: _setLadderForTest,
    injectPlan: _injectPlanForTest,
  };
}
